// ── imports explicites (C4 — générés par esm-codemod --analyze, maintenus à la main) ──
import { useCallback, useEffect, useRef, useState } from "../00-boot.jsx";
import { PRESENCE_LONGUE_MS } from "./usePresence.jsx";
import { OTHER_MAP, OTHER_SUBCATS, langPick, lieuActif, lieuAppareilLu, norm, optionLabel, rayonLieu } from "../01-taxonomy-cities.jsx";
import { localeForLang } from "../02-chat-translate-time.jsx";
import { ACTIVE_LANG, SEED, T, tx } from "../04-i18n-dict.jsx";
import { FEED_PROFILE_COLS, isUUID, listingEqualExceptViews, listingFromDB, newUUID, profileFromDB } from "../05-ui-atoms-theme.jsx";
import { CACHE_ITEMS_KEY, CACHE_ITEMS_SYNC_KEY, FEED_LISTING_SAMPLE_SELECT, FEED_LISTING_SELECT, alertFromDB, alertToDB, lanacLeanSampleOn, lnaRenommerEchantillon, lanacNearbyServerOn, rpcIncrementListingViews, rpcFilProfil, lanacFilRangs } from "../07-trust-moderation.jsx";
import { lanacEpoqueSession, lanacRpc, lanacSupabaseClient, lanacTropLong, lanacUidRange, lanacWrite } from "../08-supabase-client.jsx";
// ── Tranche 5d · useFeedState : annonces, filtres, recherche, géo, pagination ─
//    La plus grosse tranche (53 états) — déclarations copiées telles quelles,
//    ordre relatif préservé. Les effets feed (fetch, sync delta, realtime
//    listings) restent dans App() avec leurs helpers.
const LISTING_PAGE_SIZE=24; // affichage incrémental de la grille (paquets de 24) — consommé aussi par les callbacks d'App()
//  C11 (i) · L'historique de recherche vu par l'ÉCRAN, la même règle que la base : une recherche par étiquette NORMALISÉE
//   (`norm` ≡ `public.search_label_norm`), la plus récente gagne, la plus récente d'abord, dix-huit au plus. PURE : elle sert
//   aux mises à jour d'état, qui n'écrivent plus rien en base.
const HISTORIQUE_MAX=18;
const fusionnerHistorique=(...listes)=>{
  const vu=new Map();
  for(const s of listes.flat()){
    if(!s||!s.label) continue;
    const k=norm(String(s.label).trim());
    const deja=vu.get(k);
    if(!deja||(Number(s.ts)||0)>(Number(deja.ts)||0)) vu.set(k,s);
  }
  return [...vu.values()].sort((a,b)=>(Number(b.ts)||0)-(Number(a.ts)||0)).slice(0,HISTORIQUE_MAX);
};
// ── LE FIL DU MEMBRE, CHOISI PAR LE SERVEUR (07/10/2026, A20 — accord de Mounir : « le mieux serait de gérer via un serveur »,
//    « egress attention à ne jamais augmenter ») ─────────────────────────────────────────────────────────────────────────
//  L'échantillon de l'accueil était « les 200 plus récentes de la base » : « du plus proche au plus loin » n'était donc vrai que
//  dans ces 200. Pour un membre dont le fil est allumé et le lieu connu, la MÊME lecture (la sonde id + updated_at, ou la
//  projection maigre au premier chargement — 200 lignes au plus) passe par `fil_echantillon` : le serveur choisit, dans TOUTE
//  la base et avec la règle du téléphone, les annonces que son fil montrerait en premier ; le téléphone continue de ranger
//  (une seule règle : rangerParAnneaux + ordreDuFil). Tout le reste du chemin est inchangé — le cache, le delta, le garde-fou
//  des 80 % — et rien ne coûte une requête de plus :
//   · le lieu part ARRONDI à 0,01° (≈ 1 km ; l'anneau le plus fin en fait 5) : le serveur CHOISIT, il ne range pas ;
//   · les centres d'intérêt sont ceux de la VISITE (figés : les cartes ne bougent pas sous le doigt) ; rien ne part si le
//     membre a coupé la personnalisation ;
//   · un échec de la fonction retombe sur la lecture d'avant (les plus récentes) — jamais un accueil vide ;
//   · LA LECTURE D'AVANT LA SESSION (celle qui part au démarrage, avant que le membre soit connu, pour que l'accueil soit
//     prêt à la fin de l'écran d'ouverture) est celle DU FIL quand le cache de cet appareil est l'échantillon du fil de CE
//     membre : ses paramètres sont gardés avec le cache (FIL_CACHE_CLE : {uid, sonde}), et la session en mémoire doit être la
//     sienne. Sinon : les plus récentes, comme avant. Le nombre de requêtes et leur instant ne changent pas ; mais les deux
//     lectures d'un démarrage nomment le MÊME ensemble — sans quoi, au-delà de 200 annonces visibles, elles se disputeraient
//     le cache (chacune rechargerait ce que l'autre vient d'écarter) ;
//   · un changement de lieu ne redemande l'échantillon que s'il était COUPÉ (200 lignes) : en dessous, il est toute la base
//     visible, la réponse serait la même.
//  (12/10, A38) LE SERVEUR SAIT. Les centres d'intérêt ne sont plus calculés ni gardés sur l'appareil : le profil vit dans la
//  base (fil_profil_de, à partir des recherches, favoris, alertes et annonces consultées du membre — les mêmes sur tous ses
//  appareils) et `fil_echantillon` rend les annonces DANS L'ORDRE DE SON SCORE. Le téléphone garde LE RANG de chaque annonce de
//  la réponse (`filRang`, posé dans finalize, gardé avec le cache) et range avec lui (ordreDuFil). Ce qui part au serveur : le
//  lieu arrondi, le rayon, l'interrupteur de la personnalisation (p_perso), la limite — plus aucune clé.
//  La clé de l'appareil : l'interrupteur, rattaché au membre (allumé par défaut ; « 0 » le coupe pour CE membre sur CET appareil).
const FIL_PERSO_CLE=uid=>`lanac:filPerso:${uid}`;
const FIL_CACHE_CLE="lanac:cache:home:fil"; // {uid, sonde} : le cache de l'accueil est l'échantillon du fil de CE membre, lu avec CES paramètres
//  Le cache garde l'échantillon ET ses propres annonces (lues à part) : au-delà de 200 lignes, les plus anciennes tombaient
//  du cache et étaient redemandées à CHAQUE démarrage. Plafond : 60 de plus.
//  (04/11, B-ACCUEIL-1) 60 de plus ne suffisaient pas : le compte A (132 annonces) en a 332 dans son accueil — ses 72 plus
//   anciennes étaient relues en ENTIER à chaque démarrage. Le plafond couvre l'échantillon et un vendeur actif (~1 Ko la ligne).
const HOME_CACHE_PLAFOND=1200;
//  … et borné en OCTETS (revue d'architecte) : au-delà d'1,5 Mo, l'accueil gardé reprendrait la place de la carte du membre et des caches de
//   la messagerie (une écriture refusée est silencieuse) — il retombe alors aux 260 lignes d'avant.
const HOME_CACHE_OCTETS=1500000;
const filPersoOn=uid=>{ try{ return !!uid&&localStorage.getItem(FIL_PERSO_CLE(uid))!=="0"; }catch(e){ return !!uid; } };
//  L'ancienne mémoire des centres d'intérêt de l'appareil (A20, 07/10) n'a plus de lecteur : effacée quand on la rencontre.
const oublierInteretsDAvant=uid=>{ try{ if(uid) localStorage.removeItem(`lanac:filInterets:${uid}`); }catch(e){} };
const reglerFilPerso=(uid,on)=>{
  if(!uid) return;
  try{ if(on) localStorage.removeItem(FIL_PERSO_CLE(uid)); else localStorage.setItem(FIL_PERSO_CLE(uid),"0"); }catch(e){}
  //  la lecture gardée avec le cache suit l'interrupteur
  try{ const m=JSON.parse(localStorage.getItem(FIL_CACHE_CLE)||"null"); if(m&&typeof m==="object"&&m.uid===uid&&m.sonde&&typeof m.sonde==="object"){ m.sonde.p_perso=!!on; localStorage.setItem(FIL_CACHE_CLE,JSON.stringify(m)); } }catch(e){}
};
//  LA MÉMOIRE DES RANGS (id → rang du serveur, `lanacFilRangs` dans la passerelle, purgée au changement de compte) : posée à chaque
//  lecture du fil (finalize), semée depuis le cache au démarrage ; lue par ordreDuFil. Une mémoire de module, pas une propriété
//  d'objet : l'objet d'une annonce est remplacé par la complétion des favoris, l'hydratation d'une fiche, le temps réel — et
//  perdait son rang (vu par S146 le 12/10).
const rangDuFil=id=>lanacFilRangs.get(String(id));
const arrondiDuFil=x=>Math.round(Number(x)*100)/100;
//  Les paramètres de la lecture du fil — ou null : pas de membre, pas de lieu. PURE (banc l-ordre-du-fil). (29/10/2026) plus d'interrupteur
//  « allumé » : le fil est l'accueil de tous (l'accueil classique est retiré).
const sondeDuFil=({uid=null,lieu=null,perso=true,limite=200}={})=>{
  if(!uid||!lieu||!Number.isFinite(lieu.lat)||!Number.isFinite(lieu.lng)) return null;
  return {p_lat:arrondiDuFil(lieu.lat),p_lng:arrondiDuFil(lieu.lng),p_rayon_km:rayonLieu(lieu.rayon),p_perso:perso!==false,p_limite:limite};
};
//  Ce qui fait qu'une lecture répond à un LIEU (l'interrupteur ne déplace que l'ordre : il n'y entre pas).
const signatureDuFil=s=>s?`${s.p_lat},${s.p_lng},${s.p_rayon_km}`:"";
//  LA LECTURE GARDÉE avec le cache : {uid, fil} ou null. Ce qui est lu d'une mémoire n'est jamais cru — des nombres finis, un
//  rayon de la pastille, l'interrupteur tel qu'il est SUR CET APPAREIL (jamais la valeur gardée), la limite du téléphone.
const sondeGardee=(limite=200)=>{
  try{
    const m=JSON.parse(localStorage.getItem(FIL_CACHE_CLE)||"null"), s=m&&typeof m==="object"?m.sonde:null;
    if(!s||typeof s!=="object"||typeof m.uid!=="string"||!m.uid||typeof s.p_lat!=="number"||typeof s.p_lng!=="number"||!Number.isFinite(s.p_lat)||!Number.isFinite(s.p_lng)) return null;
    return {uid:m.uid,fil:{p_lat:arrondiDuFil(s.p_lat),p_lng:arrondiDuFil(s.p_lng),p_rayon_km:rayonLieu(s.p_rayon_km),p_perso:filPersoOn(m.uid),p_limite:limite}};
  }catch(e){ return null; }
};
//  AVANT LA SESSION : la lecture du fil gardée, SI la session en mémoire est celle du membre à qui est ce cache (lecture
//  locale : aucune requête) ; tout autre cas — pas de marque, pas de session, un autre membre, une erreur — : les plus récentes.
//  (29/10/2026) le test « allumé pour CE membre » (le forçage, le coupe-circuit) est retiré avec l'accueil classique.
const avantLaSession=(client,limite=200)=>{
  const g=sondeGardee(limite);
  if(!g||!client||!client.auth||typeof client.auth.getSession!=="function") return Promise.resolve({fil:null,uid:null});
  return Promise.resolve().then(()=>client.auth.getSession()).then(r=>{
    const u=r&&r.data&&r.data.session&&r.data.session.user?r.data.session.user.id:null;
    return (u&&u===g.uid)?{fil:g.fil,uid:g.uid}:{fil:null,uid:null};
  },()=>({fil:null,uid:null}));
};
// ── LE FIL À JOUR (B-ACCUEIL-1, 04/11/2026) ─────────────────────────────────────────────────────────────────────────────
//  POURQUOI. Chaque démarrage chaud retéléchargeait l'index ENTIER de l'accueil (la sonde id + updated_at des 200 annonces du fil,
//   celle de SES annonces, puis leurs vues : trois requêtes, ~24 Ko mesurés par S130), même quand rien n'avait changé.
//  LA RÈGLE (un « 304 » par seau, sans état ni horloge) : l'appareil range ce qu'il GARDE en 16 seaux (id mod 16), chaque seau
//   « id:ms » trié par id (ms = l'heure de la mise à jour, TRONQUÉE à la ms comme floor(epoch × 1000) dans la base), et envoie le
//   SHA-256 (16 premiers hexa) de chacun, plus celui de l'ordre qu'il garde (« o | m »). `fil_a_jour` calcule la même chose sur ce
//   qui est vrai maintenant, sous la RLS du membre, et ne rend que les paires (id, ms) des seaux qui diffèrent, l'ordre s'il a changé,
//   et les vues alignées sur o ‖ m. Une mémoire abîmée ne coûte qu'une réponse plus longue — jamais un changement perdu : la
//   comparaison des heures est EXACTE, comme la sonde d'avant. Toute réponse douteuse (ou une erreur) : l'ancienne sonde, telle quelle.
//  La mémoire de l'ordre ({uid, sig, o, m}) est écrite par finalize APRÈS le cache, par la lecture qui l'a bâti ; elle dit aussi à
//   qui est le cache — la sortie d'un membre efface son accueil (oublierCarte). Banc : tests/l-accueil-ne-relit-que-ce-qui-change.
const FIL_ORDRE_CLE="lanac:fil:ordre";
const FIL_SEAUX=16;
const FIL_ORDRE_MAX=5000;
//  « 2026-11-03T21:30:00.123456+00:00 » (PostgREST) → ms, tronquées ; null si illisible
const msDeLaMaj=s=>{
  if(typeof s!=="string") return null;
  const m=s.match(/^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}(?::?\d{2})?)?$/);
  if(!m) return null;
  const frac=((m[2]||"")+"000").slice(0,3);
  let tz=m[3]||"Z";
  if(/^[+-]\d{2}$/.test(tz)) tz+=":00"; else if(/^[+-]\d{4}$/.test(tz)) tz=tz.slice(0,3)+":"+tz.slice(3);
  const t=Date.parse(m[1].replace(" ","T")+"."+frac+tz);
  return Number.isFinite(t)?t:null;
};
const seauDuFil=id=>Number(id)%FIL_SEAUX;
//  [{id, ms}] → 16 textes « id:ms,id:ms » (triés par id) — le texte que la base hache
const textesDesSeaux=lignes=>{
  const s=Array.from({length:FIL_SEAUX},()=>[]);
  for(const l of lignes) s[seauDuFil(l.id)].push(l);
  return s.map(x=>x.sort((a,b)=>a.id-b.id).map(l=>`${l.id}:${l.ms}`).join(","));
};
//  SHA-256 hexadécimal ; null sans crypto.subtle (contexte non sûr) — jamais une empreinte inventée
const empreinteDuTexte=t=>{
  const sub=globalThis.crypto&&globalThis.crypto.subtle;
  if(!sub||typeof TextEncoder==="undefined") return Promise.resolve(null);
  return sub.digest("SHA-256",new TextEncoder().encode(t)).then(h=>[...new Uint8Array(h)].map(b=>b.toString(16).padStart(2,"0")).join(""),()=>null);
};
//  ce dont l'ORDRE dépend : le lieu, le rayon, la personnalisation, la limite
const sigOrdre=s=>s?`${signatureDuFil(s)},${s.p_perso?1:0},${s.p_limite}`:"";
const entiersDuFil=a=>Array.isArray(a)&&a.length<=FIL_ORDRE_MAX&&a.every(x=>Number.isSafeInteger(x)&&x>0);
const ordreGarde=(uid,sig)=>{
  try{
    const g=JSON.parse(localStorage.getItem(FIL_ORDRE_CLE)||"null");
    if(!g||g.v!==1||typeof uid!=="string"||!uid||g.uid!==uid||g.sig!==sig||!entiersDuFil(g.o)||!entiersDuFil(g.m)) return null;
    return {o:g.o,m:g.m};
  }catch(e){ return null; }
};
const ecrireOrdre=(uid,sig,o,m)=>{ try{ localStorage.setItem(FIL_ORDRE_CLE,JSON.stringify({v:1,uid:uid||null,sig:sig||"",o,m})); return true; }catch(e){ return false; } };
//  ce qu'il faut relire : ce qui manque au cache, et une paire dont l'heure DIFFÈRE de celle gardée (la base fait foi)
const besoinsDuFil=(enCache,ids,paires)=>{
  const maj=new Map();
  if(Array.isArray(paires)) for(let i=0;i+1<paires.length;i+=2) maj.set(String(paires[i]),Number(paires[i+1]));
  const need=new Set();
  for(const id of ids){
    const k=String(id), cur=enCache.get(k);
    if(!cur){ need.add(k); continue; }
    if(maj.has(k)&&msDeLaMaj(cur.updatedAt)!==maj.get(k)) need.add(k);
  }
  return [...need];
};
//  L'APPEL : {o, m, p, v, sonde, uid} — ou null (repli : l'ancienne sonde). Ne rejette jamais.
const filAJour=(client,fil,uid,cachedById)=>{
  const sig=sigOrdre(fil), g=ordreGarde(uid,sig);
  const ens=g?new Set([...g.o,...g.m].map(String)):null, lignes=[];
  cachedById.forEach((it,k)=>{
    if(ens&&!ens.has(k)) return;
    const ms=msDeLaMaj(it&&it.updatedAt), id=Number(k);
    if(ms!=null&&Number.isSafeInteger(id)&&id>0) lignes.push({id,ms});
  });
  return Promise.all([...textesDesSeaux(lignes).map(empreinteDuTexte),g?empreinteDuTexte(g.o.join(",")+"|"+g.m.join(",")):null])
    .then(h=>{
      const seaux=h.slice(0,FIL_SEAUX);
      return client.rpc("fil_a_jour",{...fil,p_seaux:seaux.every(x=>typeof x==="string")?seaux.map(x=>x.slice(0,16)):null,p_empreinte:h[FIL_SEAUX]||null});
    })
    .then(r=>{
      const d=r&&!r.error?r.data:null;
      if(!d||typeof d!=="object"||!Array.isArray(d.p)||d.p.length%2||!d.p.every(x=>Number.isSafeInteger(x))||!Array.isArray(d.v)) return null;
      const o=entiersDuFil(d.o)?d.o:(d.o===undefined&&g?g.o:null), m=entiersDuFil(d.m)?d.m:(d.m===undefined&&g?g.m:null);
      if(!o||!m) return null;
      return {o,m,p:d.p,v:d.v.length===o.length+m.length?d.v:null,sonde:fil,uid};
    },()=>null);
};
// ── fin du fil à jour ──
/** @param {any} me @param {{view?:string, lang?:string, setViewedHistory?:Function, isMobile?:boolean}} [ports]
 *  Ports (BLOC2-1) : symboles d'AUTRES hooks, tous appelés AVANT celui-ci
 *  (useUiPrefs → lang, useNav → view/setViewedHistory, useIsMobile → isMobile)
 *  — aucun réordonnancement. */
function useFeedState(me, ports={}){
  const {view,lang,setViewedHistory,isMobile,setAllUsers}=ports;
  // BLOC1-4 · Réglages & refs du feed (déplacés depuis App(), RETOURNÉS car App
  //   les consomme encore : loadHomeSample a 10 usages… (feedPaginationOn : ÉLAGUÉ le
  //   01/08, B4 — la pagination serveur est le seul chemin, plus aucun usage).
  //   Retourner un symbole depuis son hook DISSOUT la grappe : les effets qui le
  //   partageaient deviennent migrables un par un, sans big-bang.
  const HOME_SAMPLE_SIZE=200; // WS6 : échantillon récent pour les rails accueil quand la pagination serveur est active
  // WS8 — pagination serveur ACTIVE par défaut. (Historique : kill-switch "lanac:feedPagination"="0" → revient
  // à l'ancien comportement (chargement complet + sync delta), sans redéploiement. "1" ou absent → activé.)
  // ── EGRESS B4→C1 (01/08/2026) · KILL-SWITCH RETIRÉ, PUIS BRANCHES ÉLAGUÉES ───────────────
  //  B4 a supprimé le chemin de repli (sync complète O(N) de `listings`) et épinglé
  //  `feedPaginationOn=true` ; C1 a élagué les 35 branches OFF devenues du code prouvé mort,
  //  puis la constante elle-même (plus aucun lecteur). La pagination serveur n'est plus un
  //  mode : c'est LE chemin. Un `lanac:feedPagination` résiduel est purgé par l'hygiène B4.
  const canSeeListingRef=useRef(null); // WS7 : référence à jour de canSeeListing pour les handlers realtime (évite stale-closure)
  const homeSampleReqRef=useRef(0);    // Pull-to-refresh : anti-réponse-périmée pour l'échantillon accueil
  const [items,setItems]=useState(SEED);
  // C7 · T14 (16/09) : pour QUEL compte l'échantillon d'accueil a été posé (undefined = pas encore). Avant ce signal,
  //  « absente de items » ne veut rien dire — l'échantillon arrive : aucune annonce n'est résolue au serveur pour l'écran.
  const [homeSampleFor,setHomeSampleFor]=useState(undefined);
  const [sel,setSel]=useState(null);
  const [filterCat,setFilterCat]=useState(null);
  const [filterAdType,setFilterAdType]=useState(null);
  const [filterOther,setFilterOther]=useState(null);
  const [filterOtherSub,setFilterOtherSub]=useState("");
  const [filterCountry,setFilterCountry]=useState("all");
  const [filterCity,setFilterCity]=useState("all");
  const [radiusKm,setRadiusKm]=useState(0);
  const [filterCoords,setFilterCoords]=useState(null); // Phase 0 : origine de lieu (coords {lat,lng}) — défaut null → repli findCity (comportement identique, 0 régression)
  const [srch,setSrch]=useState("");
  const [maxP,setMaxP]=useState(5000000);
  const [filterStatus,setFilterStatus]=useState("all");
  // A3 (Feed Ph4) · tri EXPOSÉ. null = « Pertinence » (auto : proximité si origine, sinon confiance).
  //  "recent" / "price" forcent le mode serveur (dormant en RPC jusqu'ici). Sans origine ils trient
  //  globalement ; avec un rayon actif ils trient DANS le rayon (« le moins cher à 25 km »).
  const [feedSortPref,setFeedSortPref]=useState(null);
  const [mapHover,setMapHover]=useState(null);
  const [aiInp,setAiInp]=useState("");
  const [aiLoad,setAiLoad]=useState(false);
  const [aiResults,setAiResults]=useState([]);
  const [visualSearch,setVisualSearch]=useState(null);
  const [availabilityWatch,setAvailabilityWatch]=useState([]);
  // Phase 5C — load availability watches from Supabase (user-specific, overwrites localStorage)
  useEffect(()=>{
    const client=lanacSupabaseClient();
    if(!client||!me?.id) return;
    // Borne A5 : RLS = mes suivis de disponibilité ; plafond 200.
    client.from("availability_watches").select("*").order("ts",{ascending:false}).limit(200).then(({data,error})=>{
      if(!error&&Array.isArray(data))setAvailabilityWatch(data.map(r=>({id:`w${r.listing_id}`,itemId:Number(r.listing_id),userId:r.user_id,title:r.title||"",ts:Number(r.ts||0)||new Date(r.created_at||0).getTime()})).filter(r=>r.itemId));
    });
  },[me?.id,me?.gen]);
  // V3-T3a · miroirs localStorage des suivis de disponibilité (même ordre : fetch puis hydrate).
  useEffect(()=>{if(!me)return;try{setAvailabilityWatch(JSON.parse(localStorage.getItem(`lanac:availableWatch:${me.id}`)||"[]"));}catch{setAvailabilityWatch([]);}},[me?.id]);
  useEffect(()=>{if(me)try{localStorage.setItem(`lanac:availableWatch:${me.id}`,JSON.stringify(availabilityWatch));}catch(e){}},[availabilityWatch,me?.id]); // try/catch 28/08 : quota plein ⇒ pas d'écran d'erreur persistant
  const [searchMemory,setSearchMemory]=useState([]);
  //  ⭐ 26/09/2026 (C11 (i)) — L'HISTORIQUE DE RECHERCHE : UNE RÈGLE EN BASE, UNE REQUÊTE PAR DÉMARRAGE, DES ORDRES EN FILE.
  //   · L'écran s'affiche TOUJOURS depuis le miroir local (effets plus bas) — aucune attente.
  //   · Au démarrage, UNE requête CONDITIONNELLE (`search_history_sync`) : l'appareil donne l'empreinte qu'il connaît ; même
  //     empreinte ⇒ « rien de neuf » (~60 octets) ; sinon la liste, colonnes utiles. Avant : `select *` à CHAQUE démarrage.
  //   · L'historique d'avant la synchronisation part UNE fois, avec cette requête, et la BASE le fusionne. Avant : une
  //     « migration unique » écrite DANS une mise à jour d'état (dette (bm)) — une recherche faite pendant la lecture la
  //     faisait refuser en bloc (409), et l'ancien historique disparaissait au démarrage suivant.
  //   · Chaque ordre (synchroniser, retenir, oublier, tout effacer) part EN FILE, dans l'ordre des gestes : « tout effacer »
  //     puis une recherche arrivent dans cet ordre, jamais l'inverse. Rien n'écrit en base depuis une mise à jour d'état.
  const histFileRef=useRef({user:null,chaine:Promise.resolve(),enAttente:new Map()});
  const histEmpreinteCle=uid=>`lanac:searchHistoryFp:${uid}`;
  const histEnFile=(uid,fn)=>{
    const f=histFileRef.current;
    if(!uid||f.user!==uid) return Promise.resolve(null);
    const p=f.chaine.then(fn);
    f.chaine=p.then(()=>{},()=>{});
    return p;
  };
  const histRetenirEmpreinte=(uid,fp)=>{if(typeof fp==="string"&&fp){try{localStorage.setItem(histEmpreinteCle(uid),fp);}catch(_){}}};
  useEffect(()=>{
    if(!me?.id) return;
    const uid=me.id;
    histFileRef.current={user:uid,chaine:Promise.resolve(),enAttente:new Map()};
    let connue=null,local=[];
    try{connue=localStorage.getItem(histEmpreinteCle(uid));}catch(_){}
    try{local=JSON.parse(localStorage.getItem(`lanac:searchMemory:${uid}`)||"[]");}catch(_){}
    //  Un appareil qui n'a JAMAIS synchronisé propose son historique local ; la base décide (une fois, par fusion, et jamais
    //   après que le membre a géré son historique). Une ligne au-delà d'un plafond (T19) reste locale et c'est dit (T20).
    const lignes=!connue&&Array.isArray(local)?local.filter(s=>s&&s.label).slice(0,18).map(s=>({id:isUUID(s.id)?s.id:newUUID(),...alertToDB(s)})):[];
    const aImporter=lignes.filter(r=>!lanacTropLong("search_history",{...r,user_id:uid}));
    if(aImporter.length<lignes.length){try{window.__lncReport?.("search_history_too_long",`${lignes.length-aImporter.length}/${lignes.length} ligne(s) au-delà du plafond, gardées en local`);}catch(_){}}
    histEnFile(uid,async()=>{
      const r=await lanacRpc({what:"historique de recherche (synchronisation)",name:"search_history_sync",args:{p_known:connue,p_import:aImporter.length?aImporter:null},idempotent:true});
      if(!r.ok||histFileRef.current.user!==uid) return;
      const d=r.data||{};
      histRetenirEmpreinte(uid,d.fp);
      if(d.same||!Array.isArray(d.rows)) return;
      //  les recherches faites PENDANT cette requête (en file derrière elle) restent à l'écran : elles partent juste après
      setSearchMemory(fusionnerHistorique(d.rows.map(alertFromDB).filter(Boolean),[...histFileRef.current.enAttente.values()]));
    });
  },[me?.id,me?.gen]);
  // V3-T3a · miroirs localStorage de l'historique de recherche. L'hydratation locale est
  // SYNCHRONE et s'exécute avant que la réponse de search_history n'arrive : c'est ce qui
  // permet à la migration one-shot ci-dessus de voir le local dans `prev`. Ordre conservé.
  useEffect(()=>{if(!me)return;try{setSearchMemory(JSON.parse(localStorage.getItem(`lanac:searchMemory:${me.id}`)||"[]"));}catch{setSearchMemory([]);}},[me?.id]);
  useEffect(()=>{if(me)try{localStorage.setItem(`lanac:searchMemory:${me.id}`,JSON.stringify(searchMemory));}catch(e){}},[searchMemory,me?.id]); // try/catch 28/08 : quota plein ⇒ pas d'écran d'erreur persistant
  const [topSearch,setTopSearch]=useState("");
  const [searchTab,setSearchTab]=useState("articles");
  const [browseCat,setBrowseCat]=useState(null);
  const [browseMode,setBrowseMode]=useState(null);
  const [mobileFilterPanel,setMobileFilterPanel]=useState(null);
  const [mobileFilterReturn,setMobileFilterReturn]=useState("market");
  const [mobileFilters,setMobileFilters]=useState({cat:null,adType:null,propType:"",rooms:"",brand:"",fuel:"",workType:"",other:"",otherSub:"",country:"all",city:"all",coords:null,radius:"0",max:"",near:false,query:""});
  const [filterRooms,setFilterRooms]=useState(null);
  const [filterPropType,setFilterPropType]=useState("");
  const [filterBrand,setFilterBrand]=useState("");
  const [filterFuel,setFilterFuel]=useState("");
  const [filterWorkType,setFilterWorkType]=useState("");
  const [listingVisibleCount,setListingVisibleCount]=useState(LISTING_PAGE_SIZE);
  const [nearSearch,setNearSearch]=useState("");
  const [nearCat,setNearCat]=useState(null);
  const [nearRadiusKm,setNearRadiusKm]=useState(25);
  // ── A2a (Feed Ph4) · GPS « utiliser ma position » — MÉMORISÉ localement, opt-in ──────────
  //  AVANT : la position mourait au reload → l'utilisateur re-tapait le GPS à CHAQUE session.
  //  MAINTENANT : après un GPS réussi (geste EXPLICITE), on garde {lat,lng,ts} en localStorage —
  //  JAMAIS envoyé au serveur (l'invariant vie privée est CONSERVÉ : rien ne sort de l'appareil),
  //  TTL 24 h (au-delà on redemande une position fraîche). Au boot, si une position fraîche existe,
  //  on la restaure ET on réactive « autour de moi » → proximité fine DÈS l'ouverture, sans geste.
  //  Kill-switch `lanac:gpsPersist="0"` (ni lecture ni écriture). clearMyPosition efface la clé.
  const GPS_KEY="lanac:gps", GPS_TTL_MS=24*3600*1000;
  const gpsPersistOn=(()=>{try{return localStorage.getItem("lanac:gpsPersist")!=="0";}catch(e){return true;}})();
  const _gpsHydrate=(()=>{
    if(!gpsPersistOn) return null;
    try{const r=JSON.parse(localStorage.getItem(GPS_KEY)||"null");
      if(r&&Number.isFinite(r.lat)&&Number.isFinite(r.lng)&&Date.now()-Number(r.ts||0)<GPS_TTL_MS) return {lat:r.lat,lng:r.lng};
    }catch(e){}
    return null;
  })();
  const [gpsPos,setGpsPos]=useState(_gpsHydrate); // {lat,lng} ou null (hydraté du dernier GPS mémorisé, < 24 h)
  const [gpsStatus,setGpsStatus]=useState(_gpsHydrate?"on":"idle"); // idle | loading | on | denied | unavailable
  // "Autour de moi" : quand actif + position connue, le FEED marché et "Pour vous" sont orientés par le GPS réel.
  //  Restauré à `true` si une position mémorisée fraîche a été hydratée (l'utilisateur l'avait activé).
  const [nearMe,setNearMe]=useState(!!_gpsHydrate);
  // ── LE LIEU CHOISI SUR L'APPAREIL (02/10/2026, lot B — la pastille de lieu) ─────────────────────────────────────────────
  //  Deux choix explicites, un seul à la fois (« le dernier choix ») : la POSITION (ci-dessus, `lanac:gps`, 24 h) ou une
  //  VILLE choisie dans la pastille (`lanac:lieu`, sans échéance : une ville n'est pas une position). Le rayon vit avec.
  //  Rien ne part au serveur : comme la position, le choix reste sur l'appareil, et il est rattaché au membre qui l'a posé
  //  (`lieuAppareilLu` ne rend rien du choix d'un autre compte). `gpsVille` = le NOM de la position ({nom,pays}), lu une fois
  //  par /geocode inverse — la fonction que le dépôt d'annonce utilise déjà — pour que la pastille dise « Paris », pas un point.
  const LIEU_KEY="lanac:lieu";
  const [gpsVille,setGpsVille]=useState(()=>{
    if(!_gpsHydrate) return null;
    try{const r=JSON.parse(localStorage.getItem(GPS_KEY)||"null");return (r&&typeof r.ville==="string"&&r.ville)?{nom:r.ville,pays:(typeof r.pays==="string"&&r.pays)?r.pays:"all"}:null;}catch(e){return null;}
  });
  const [lieuBrut,setLieuBrut]=useState(()=>{try{return JSON.parse(localStorage.getItem(LIEU_KEY)||"null");}catch(e){return null;}});
  const lieuPref=lieuAppareilLu(lieuBrut,me?.id); // {ville:{nom,pays,lat,lng}|null, rayon} — dérivé au rendu, jamais d'effet
  const gpsReqRef=useRef(0);                      // une réponse de position ou de nom arrivée après un autre choix est ignorée
  const ecrireLieu=patch=>{
    const uid=me?.id; if(!uid) return;
    setLieuBrut(prev=>{
      const p=lieuAppareilLu(prev,uid);
      const suivant={uid,ville:p.ville,rayon:p.rayon,...patch,ts:Date.now()};
      try{localStorage.setItem(LIEU_KEY,JSON.stringify(suivant));}catch(e){}
      return suivant;
    });
  };
  const clearMyPosition=()=>{gpsReqRef.current++;setGpsPos(null);setGpsStatus("idle");setNearMe(false);setGpsVille(null);try{localStorage.removeItem(GPS_KEY);}catch(e){}};
  //  Une ville choisie REMPLACE la position (et inversement, dans requestMyPosition) : la pastille n'a qu'une réponse.
  const choisirLieuVille=(nom,pays,coords)=>{
    if(!nom||!coords||!Number.isFinite(coords.lat)||!Number.isFinite(coords.lng)) return;
    clearMyPosition();
    ecrireLieu({ville:{nom:String(nom),pays:pays||"all",lat:coords.lat,lng:coords.lng}});
  };
  const choisirRayonLieu=km=>ecrireLieu({rayon:rayonLieu(km)});
  //  « Ma ville » : plus aucun choix sur l'appareil — la règle retombe sur la ville du profil, puis la position approximative.
  const oublierLieuChoisi=()=>{clearMyPosition();ecrireLieu({ville:null});};
  const [showGeoPrompt,setShowGeoPrompt]=useState(false); // invite douce post-login (une seule fois)
  // Géo-IP Cloudflare (couche 1, GRATUITE via Pages Function /geo) : origine PAR DÉFAUT du feed quand ni GPS
  // ni ville filtrée. GPS et ville gardent TOUJOURS la priorité. `null` → tri 'trust' inchangé (zéro régression).
  const [ipGeo,setIpGeo]=useState(null);
  // V3-T3b · Géo-IP (Pages Function /geo) : origine par défaut du feed.
  useEffect(()=>{
    let cancelled=false;
    fetch("/geo",{cache:"no-store"}).then(r=>r.ok?r.json():null).then(g=>{
      if(cancelled||!g) return;
      const lat=Number(g.lat),lng=Number(g.lng);
      setIpGeo({country:g.country||null,city:g.city||null,lat:Number.isFinite(lat)?lat:null,lng:Number.isFinite(lng)?lng:null,timezone:g.timezone||null});
    }).catch(()=>{});
    return()=>{cancelled=true;};
  },[]);
  // V3-T3b · Invite douce "voir les annonces près de chez vous ?" après connexion — UNE seule fois (mémorisée), opt-in.
  useEffect(()=>{
    if(!me?.id||gpsPos) return;
    let answered=false; try{answered=localStorage.getItem("lanac:geoPrompt")==="1";}catch(e){}
    if(answered) return;
    const t=setTimeout(()=>setShowGeoPrompt(true),1500);
    return()=>clearTimeout(t);
  },[me?.id]);
  const [feedItems,setFeedItems]=useState([]);
  const [feedLoading,setFeedLoading]=useState(false);
  const [feedHasMore,setFeedHasMore]=useState(false);
  const [feedError,setFeedError]=useState(false);
  const [feedTotal,setFeedTotal]=useState(null);
  // Ph5A-2 · FACETTES : compteurs par valeur de sous-filtre pour le contexte de filtre APPLIQUÉ
  //  (« BMW (12) »). Dormant si `lanac:facets="0"`. null = pas de compteurs (catégorie sans
  //  sous-filtres, ou facettes non chargées) ⇒ l'UI n'affiche rien de plus, comportement inchangé.
  const [facetCounts,setFacetCounts]=useState(null);
  const facetsOn=(()=>{try{return localStorage.getItem("lanac:facets")!=="0";}catch(e){return true;}})();
  const [catCounts,setCatCounts]=useState(null);   // WS6 : compteurs catégories (RPC) quand flag ON
  const [mapPoints,setMapPoints]=useState(null);   // WS6 : points carte (RPC) quand flag ON
  const [searchSuggestItems,setSearchSuggestItems]=useState([]); // WS6 : suggestions annonces (RPC, débouncé) quand flag ON
  const [ptrRefreshing,setPtrRefreshing]=useState(false); // Pull-to-refresh : overlay skeleton monté
  const [ptrSkClosing,setPtrSkClosing]=useState(false);   // Pull-to-refresh : skeleton en fondu de sortie
  const [ptrSkTop,setPtrSkTop]=useState(0);               // Pull-to-refresh : position (haut du contenu) de l'overlay
  const [searchFocused,setSearchFocused]=useState(false);
  // V3-T4 · miroirs « live » des états du feed (Étape 2 realtime listings) : les handlers
  // realtime tournent dans une closure figée sur me.id → ils lisent l'état COURANT via ces
  // refs, sans re-souscrire. Affectées en corps de rendu, exactement comme dans App() ;
  // elles ne sont lues QUE par des handlers asynchrones → la position dans le rendu est
  // sans effet observable.
  // ═══ BLOC3-1 · signatures dérivées du feed + points carte ═══════════════════
  //   Ces 3 symboles ne dépendent QUE d'états de ce hook (+ ports view/isMobile
  //   et `me`) : ils rentrent chez eux. RETOURNÉS car App() s'en sert encore
  //   (listingFilterSig 5×, nearMeSig 3×, feedParamsRef 6×).
  const listingFilterSig=`${filterCountry}|${filterCity}|${radiusKm}|${filterCat}|${filterAdType}|${filterOther}|${filterOtherSub}|${filterPropType}|${filterRooms}|${filterBrand}|${filterFuel}|${filterWorkType}|${srch}|${maxP}|${filterStatus}|${feedSortPref||""}|${view}|${filterCoords?(filterCoords.lat+","+filterCoords.lng):""}|${me?.cityLat},${me?.cityLng}|${me?.city}|${me?.country}`;
  useEffect(()=>{setListingVisibleCount(LISTING_PAGE_SIZE);},[listingFilterSig]);
  //  (02/10, lot B) la VILLE choisie sur l'appareil y entre (changer de lieu recharge) ; le RAYON n'y est plus : il ne filtre
  //  plus la requête du marché, il place seulement la ligne « Plus loin » — le changer ne coûte aucun appel.
  const nearMeSig=(nearMe&&gpsPos)?`gps:${gpsPos.lat.toFixed(4)},${gpsPos.lng.toFixed(4)}`:(lieuPref.ville?`lieu:${lieuPref.ville.lat.toFixed(4)},${lieuPref.ville.lng.toFixed(4)}`:((ipGeo&&Number.isFinite(ipGeo.lat)&&Number.isFinite(ipGeo.lng))?`ip:${ipGeo.lat.toFixed(3)},${ipGeo.lng.toFixed(3)}`:"off"));
  const feedParamsRef=useRef(null);     // {params, sort} — mis à jour au rendu (après calcul des filtres)
  // Points carte (RPC) — vue carte + flag ON. EGRESS P3b (cartographie 3a) : le gate
  //  `isMobile` datait de WS6 — la carte DESKTOP retombait sur `filtered` (l'échantillon
  //  top-200) : une annonce hors échantillon était invisible sur la carte desktop alors que
  //  la RPC `listings_map_points` la sert. MapView consomme déjà `mapPoints` quand ils
  //  existent : on élargit la SOURCE, aucun changement de rendu.
  useEffect(()=>{
    if(view!=="map") return;
    const client=lanacSupabaseClient(); if(!client) return;
    const pr=feedParamsRef.current; if(!pr||!pr.params) return;
    let cancelled=false;
    setMapPoints(null); // reset : évite d'afficher d'anciens points le temps que la nouvelle réponse arrive
    client.rpc("listings_map_points",pr.params).then(({data})=>{if(!cancelled&&Array.isArray(data))setMapPoints(data);},()=>{});
    return()=>{cancelled=true;};
  },[listingFilterSig,nearMeSig,view,isMobile]);
  const selRef=useRef(null); selRef.current=sel;               // fiche ouverte (rattrapage realtime)
  const feedItemsRef=useRef(null); feedItemsRef.current=feedItems; // grille marché (sans toucher ordre/scroll)
  const itemsRef=useRef(null); itemsRef.current=items;         // annonces chargées (rails accueil / vus / favoris)
  // BLOC1-4 · Vues : source de vérité = RPC borné (listings.views est figé, cf. listing_views).
  //   Déclaré APRÈS les états/refs qu'il lit — lisible, et aucun doute de TDZ.
  // B6 (02/09) · DÉDUP — la sonde du banc a vu DEUX appels `listing_view_counts`
  //  STRICTEMENT identiques au boot : loadHomeSample tourne deux fois (avant `me`,
  //  puis à son arrivée — nécessaire pour « mes annonces ») et ré-hydratait les vues
  //  des MÊMES ids. Les vues sont décoratives et non temps-réel (contrat ci-dessus) :
  //  une même liste d'ids dans une fenêtre de 30 s ne se redemande pas. Une liste
  //  DIFFÉRENTE (favoris hors échantillon, refresh au-delà de 30 s) passe toujours.
  const lastViewHydrateRef=useRef({sig:"",at:0});
  const hydrateViewCounts=useCallback(ids=>{
    const client=lanacSupabaseClient();
    const list=[...new Set((ids||[]).map(x=>x==null?null:Number(x)).filter(Boolean))].slice(0,500);
    if(!client||!list.length) return;
    const sig=list.slice().sort((a,b)=>a-b).join(",");
    const last=lastViewHydrateRef.current;
    if(sig===last.sig&&Date.now()-last.at<30000) return;
    lastViewHydrateRef.current={sig,at:Date.now()};
    client.rpc("listing_view_counts",{p_ids:list}).then(({data})=>{
      if(!Array.isArray(data)||!data.length) return;
      const fresh=new Map(data.map(r=>[String(r.listing_id),Number(r.views||0)]));
      const bump=x=>{const v=fresh.get(String(x.id));return (v==null||Number(x.views||0)>=v)?x:{...x,views:v};};
      setItems(prev=>{let ch=false;const nx=prev.map(x=>{const b=bump(x);if(b!==x)ch=true;return b;});return ch?nx:prev;});
      setFeedItems(prev=>{let ch=false;const nx=prev.map(x=>{const b=bump(x);if(b!==x)ch=true;return b;});return ch?nx:prev;});
      setSel(prev=>prev?bump(prev):prev);
    },()=>{});
  },[]);
  // ── (07/10/2026, A20) LE FIL DU MEMBRE, CHOISI PAR LE SERVEUR — la règle est en tête de fichier (sondeDuFil) ──
  //  Le lieu : LA MÊME règle et les mêmes entrées que l'application (`_lieu`, lieuActif). Les centres d'intérêt de la VISITE :
  //  lus une fois par membre, relus seulement à SA demande (tirer pour rafraîchir, l'interrupteur) — jamais sous le doigt.
  const _lieuFil=lieuActif({gps:(nearMe&&gpsPos)?gpsPos:null,gpsVille,choisi:lieuPref.ville,me,ipGeo,rayon:lieuPref.rayon});
  const _sigLieuFil=_lieuFil?`${arrondiDuFil(_lieuFil.lat)},${arrondiDuFil(_lieuFil.lng)},${rayonLieu(_lieuFil.rayon)}`:"";
  //  (12/10) le fil est l'accueil du téléphone ET de l'ordinateur : la lecture ne regarde plus la largeur de l'écran.
  const filEntreesRef=useRef(null); filEntreesRef.current={lieu:_lieuFil};
  //  L'INSTANT de la dernière lecture ABOUTIE du fil (horloge de l'appareil) : une annonce née après, sans rang, est « arrivée
  //  depuis la lecture » et passe devant dans son anneau (ordreDuFil) ; un favori hors des 200, plus ancien, passe après.
  const [filLectureMs,setFilLectureMs]=useState(0);
  const derniereSondeRef=useRef(null); // la dernière lecture ABOUTIE de l'échantillon : {sig (son lieu, "" = les plus récentes), coupee}
  const sigDemandeeRef=useRef(null);   // le lieu de la dernière lecture DEMANDÉE (en vol ou aboutie)
  const lieuVerifRef=useRef(null);
  const sondeDuMembre=()=>{ const e=filEntreesRef.current||{}, uid=me?.id; return sondeDuFil({uid,lieu:e.lieu,perso:filPersoOn(uid),limite:HOME_SAMPLE_SIZE}); };
  // BLOC1-4 · Échantillon accueil (flag pagination ON) + son effet déclencheur : indissociables.
  const loadHomeSample=useCallback(()=>{
    const client=lanacSupabaseClient();
    if(!client) return Promise.resolve();
    //  (04/11, B-ACCUEIL-5) SANS SESSION, L'ACCUEIL NE SE LIT PAS : qui n'a pas de session ne voit que l'écran de connexion (19-app), et
    //   l'accueil se relit de toute façon à la connexion (`me` change). Mesuré (S194) : 2 lectures, 66 585 octets à froid, pour personne.
    //   Le membre connu, ou sa session RANGÉE sur l'appareil (la carte absente) : comme avant. Lecture locale, aucune requête.
    //   (revue d'architecte APRÈS) à la SORTIE : la lecture du membre encore en vol ne pose plus rien (le numéro avance), son accueil en
    //   mémoire part (le suivant ne voit pas l'ordre personnel d'un autre), « accueil lu pour » se vide (une reconnexion attend la lecture).
    if(!me?.id&&!lanacUidRange()){ ++homeSampleReqRef.current; setHomeSampleFor(null); setItems(prev=>prev.length?[]:prev); return Promise.resolve(); }
    const reqId=++homeSampleReqRef.current;
    //  (04/11, B-ACCUEIL-1, revue d'architecte) l'époque de la session au départ : une sortie (oublierCarte l'avance) pendant la lecture ne
    //   laisse pas une réponse en vol RÉÉCRIRE l'accueil gardé qu'elle vient d'effacer.
    const epoque=lanacEpoqueSession();
    //  (07/10) LA LECTURE DE L'ÉCHANTILLON — une seule, pour la sonde comme pour le premier chargement. Le fil du membre : la
    //   fonction du serveur, avec la MÊME projection ; sinon, ou si elle échoue : les plus récentes, comme avant. Le membre
    //   connu : sa lecture, décidée tout de suite. Avant la session : la lecture gardée avec le cache, si la session en
    //   mémoire est la sienne (avantLaSession). Ne rejette jamais ; rend la réponse marquée (`_fil`, et sa lecture `_sonde`, `_uid`).
    const filMembre=me?.id?sondeDuMembre():null;
    sigDemandeeRef.current=signatureDuFil(filMembre);
    const filP=me?.id?Promise.resolve({fil:filMembre,uid:me.id}):avantLaSession(client,HOME_SAMPLE_SIZE);
    if(!me?.id) filP.then(x=>{ if(reqId===homeSampleReqRef.current) sigDemandeeRef.current=signatureDuFil(x.fil); });
    const lireRecentes=cols=>client.from("listings").select(cols).order("created_at",{ascending:false}).limit(HOME_SAMPLE_SIZE).then(r=>({...r,_fil:false}),()=>({data:null,_fil:false}));
    const lireEchantillon=cols=>filP.then(({fil,uid})=>!fil?lireRecentes(cols)
      :client.rpc("fil_echantillon",fil).select(cols).then(r=>(r&&!r.error&&Array.isArray(r.data))?{...r,_fil:true,_sonde:fil,_uid:uid}:null,()=>null).then(r=>r||lireRecentes(cols)));
    // finalise : ordre (created_at desc) + vues monotones + setItems + hydrate vues + persiste le cache warm-boot.
    //  (04/11, B-ACCUEIL-1) `mien` : les ids de SES annonces lues avec l'échantillon (la mémoire de l'ordre) ; `vues` : celles reçues de
    //   fil_a_jour (id → vues) — alors listing_view_counts ne part pas pour l'accueil.
    const finalize=(merged,ech=null,mien=null,vues=null)=>{
      if(reqId!==homeSampleReqRef.current) return; // réponse périmée (id/refresh plus récent)
      merged.sort((a,b)=>Number(b.ts||0)-Number(a.ts||0));
      const prevViews=new Map((itemsRef.current||[]).map(x=>[String(x.id),Number(x.views||0)]));
      merged.forEach(it=>{const pv=prevViews.get(String(it.id));if(pv!=null&&pv>Number(it.views||0))it.views=pv;});
      if(vues) merged.forEach(it=>{ const v=vues.get(String(it.id)); if(v!=null&&v>Number(it.views||0)) it.views=v; }); // monotones, comme hydrateViewCounts
      //  (12/10, A38) LE RANG DU SERVEUR : la réponse de `fil_echantillon` (sonde ou projection) arrive DANS L'ORDRE DU SCORE —
      //   chaque annonce reçoit sa place (filRang), gardée avec le cache ; une annonce hors de la réponse n'en a pas. Une lecture
      //   « les plus récentes » (pas de fil) n'en pose aucun : ordreDuFil redevient l'ordre d'avant.
      const _rangs=new Map(); if(ech&&ech._fil&&Array.isArray(ech.data)) ech.data.forEach((r,i)=>{ if(r&&r.id!=null) _rangs.set(String(r.id),i); });
      merged.forEach(it=>{ const r=_rangs.get(String(it.id)); if(r!=null) it.filRang=r; else delete it.filRang; });
      lanacFilRangs.clear(); _rangs.forEach((r,id)=>lanacFilRangs.set(id,r));
      if(ech&&ech._fil) setFilLectureMs(Date.now());
      setItems(merged);
      setHomeSampleFor(me?.id||null);
      if(!vues) hydrateViewCounts(merged.map(it=>it.id)); // source de vérité des vues (RPC borné), non temps-réel
      // Feed-P2/P5 : cache durable warm-boot, borné, quota-safe → rendu instantané au prochain boot. Clé GLOBALE
      //  (indépendante de me.id) car le feed est PUBLIC : ainsi le delta tourne DÈS le boot, même pendant la
      //  fenêtre d'auth async où me.id est null — sinon loadHomeSample repliait sur un FULL fetch (~234 Ko) à
      //  CHAQUE boot avant que me se charge (Feed-P5 : la vraie fuite d'egress, cf. diagnostic sonde d'URL).
      //  (07/10) Le cache garde aussi ses propres annonces (plafond HOME_CACHE_PLAFOND) et DIT de qui il est : l'échantillon
      //   du fil de CE membre, lu avec CES paramètres ({uid, sonde} — la lecture d'avant la session du prochain démarrage), ou
      //   les plus récentes (pas de marque). La lecture aboutie est notée — son lieu, coupée ou non.
      //  (04/11, B-ACCUEIL-1) une SORTIE pendant la lecture (l'époque a avancé) : rien n'est gardé — l'accueil du membre vient d'être effacé.
      const _memeSession=epoque===lanacEpoqueSession();
      let _ecrit=false;
      if(_memeSession){
        let _corps=JSON.stringify(merged.slice(0,HOME_CACHE_PLAFOND));
        if(_corps.length>HOME_CACHE_OCTETS) _corps=JSON.stringify(merged.slice(0,260)); // borné en octets : les 260 lignes d'avant
        try{localStorage.setItem("lanac:cache:home",_corps);_ecrit=true;}catch(e){}
      }
      const _deFil=!!(ech&&ech._fil&&ech._sonde&&ech._uid);
      //  la marque ne survit pas à son cache : posée seulement s'il vient d'être écrit (mémoire pleine : pas de marque)
      if(_memeSession){ try{ if(_deFil&&_ecrit) localStorage.setItem(FIL_CACHE_CLE,JSON.stringify({uid:ech._uid,sonde:ech._sonde})); else localStorage.removeItem(FIL_CACHE_CLE); }catch(e){} }
      //  (04/11, B-ACCUEIL-1) LA MÉMOIRE DE L'ORDRE, de la lecture qui vient de bâtir le cache, APRÈS lui : l'ordre du fil, SES annonces par id
      //   croissant (l'ordre de la base), et À QUI est ce cache — la lecture du fil, le membre connu, ou la session RANGÉE (une lecture partie
      //   avant `me`, sous son jeton, voit ses annonces en attente) : la sortie d'un membre l'efface. Sans cache écrit : rien.
      const _o=(ech&&Array.isArray(ech.data))?ech.data.map(r=>Number(r&&r.id)).filter(x=>Number.isSafeInteger(x)&&x>0):[];
      const _m=(mien||[]).map(Number).filter(x=>Number.isSafeInteger(x)&&x>0).sort((a,b)=>a-b);
      if(_memeSession){ if(_ecrit) ecrireOrdre((_deFil?ech._uid:null)||me?.id||lanacUidRange()||null,_deFil?sigOrdre(ech._sonde):"",_o,_m); else { try{ localStorage.removeItem(FIL_ORDRE_CLE); }catch(e){} } }
      derniereSondeRef.current={sig:_deFil?signatureDuFil(ech._sonde):"",coupee:(Array.isArray(ech&&ech.data)?ech.data.length:0)>=HOME_SAMPLE_SIZE};
      if(lieuVerifRef.current) lieuVerifRef.current(); // le lieu a-t-il changé pendant la lecture ?
    };
    // COLD / repli : full fetch (comportement historique). Défini AVANT le delta pour servir de garde-fou.
    // EGRESS P3c · L'ÉCHANTILLON passe en projection MAIGRE quand `pg_lean_sample` est armé
    //  (−33 % mesuré sur les 200 annonces réelles de la prod). MES annonces gardent la
    //  projection COMPLÈTE : elles alimentent le repli hors-ligne de l'édition, où une colonne
    //  booléenne absente serait réécrite à `false`. Les objets de l'échantillon sont MARQUÉS
    //  (`lean`) : l'ouverture d'une fiche les hydrate (19-app), le marquage part avec l'hydratation.
    //  ⚠️ TOUJOURS marqués, pas seulement en projection maigre (corrigé 02/09) : même la
    //  projection « complète » du feed (FEED_LISTING_SELECT) est PARTIELLE pour la fiche —
    //  gearbox/power_hp/energy_class n'y sont pas (choix egress : les cartes ne les montrent
    //  pas). Non marqué, l'échantillon de PREMIÈRE VISITE (miroir lean pas encore appris)
    //  donnait des fiches définitivement amputées de ces champs — attrapé à l'œil le 02/09.
    const _lean=lanacLeanSampleOn();
    //  (04/10, A17 lot B) l'échantillon allégé porte deux ALIAS (b:baisse, sdn:seller_display_name — l'egress de la baisse
    //   compensé) : rendus à leurs vrais noms AVANT le mappeur.
    const _mapSample=row=>{const it=listingFromDB(lnaRenommerEchantillon(row));if(it)it.lean=true;return it;};
    const fullFetch=()=>{
      const queries=[{q:lireEchantillon(_lean?FEED_LISTING_SAMPLE_SELECT:FEED_LISTING_SELECT),lean:true}];
      if(me?.id) queries.push({q:client.from("listings").select(FEED_LISTING_SELECT).eq("owner_id",me.id),lean:false});
      return Promise.all(queries.map(x=>x.q.then(r=>({...r,_lean:x.lean}),()=>({data:null,_lean:x.lean})))).then(results=>{
        if(reqId!==homeSampleReqRef.current) return;
        const byId=new Map();
        // ⚠️ MES annonces sont fusionnées EN DERNIER : sur une annonce à moi présente dans le
        //   top-200, la version COMPLÈTE doit gagner sur la version maigre (sinon l'édition
        //   hors-ligne repartirait d'un objet tronqué).
        results.forEach(r=>{if(Array.isArray(r.data))r.data.forEach(row=>{const it=r._lean?_mapSample(row):listingFromDB(row);if(it)byId.set(String(it.id),it);});});
        if(!byId.size) return; // pas de données (offline) → on garde l'état courant
        finalize([...byId.values()],results[0],(results[1]&&Array.isArray(results[1].data))?results[1].data.map(row=>row&&row.id):null);
      });
    };
    // Feed-P3/P4 : WARM boot par SONDE D'INDEX (flag `lanac:feedWarmDelta`, ARMÉ par défaut ; mettre "0" pour
    //  désarmer). On fetch id+updated_at du top-200 courant (~20 Ko) + mes annonces, on diffe contre le cache,
    //  on re-télécharge en FULL uniquement les neuves/modifiées ; les inchangées viennent du cache. Correct par
    //  construction (la sonde EST le top-200 courant → sorties/suppressions exclues, ordre reconstruit par `ts`).
    //  ⭐ GARDE-FOU never-increase (Feed-P4) : si > 80 % des lignes sont périmées (ex. bump `updated_at` de masse —
    //  le trigger set_updated_at bumpe MÊME sur +1 vue), sonde + re-fetch quasi-total coûterait PLUS que le full
    //  → on REPLIE sur `fullFetch`. L'egress ne dépasse ainsi JAMAIS le chemin historique. Flag "0" → full direct.
    const warmDeltaOn=(()=>{try{return localStorage.getItem("lanac:feedWarmDelta")!=="0";}catch(e){return true;}})();
    let cachedById=null;
    if(warmDeltaOn){try{const c=JSON.parse(localStorage.getItem("lanac:cache:home")||"null");if(Array.isArray(c)&&c.length){cachedById=new Map();c.forEach(it=>{if(it?.id!=null)cachedById.set(String(it.id),it);});}}catch(e){}}
    if(cachedById){
      // EGRESS P3c : le re-fetch des périmées suit la projection de l'échantillon — SAUF pour
      //  MES annonces (repli d'édition), servies en complet par la requête dédiée du fullFetch.
      //  Rend id → annonce fraîche, ou null (re-fetch échoué → construit avec le cache, dégradation douce, prochain passage rattrape).
      const relire=(ids,mineIds)=>{
        if(!ids.length) return Promise.resolve(null);
        const _leanIds=ids.filter(id=>!mineIds.has(id)), _fullIds=ids.filter(id=>mineIds.has(id));
        const _qs=[];
        if(_leanIds.length) _qs.push(client.from("listings").select(_lean?FEED_LISTING_SAMPLE_SELECT:FEED_LISTING_SELECT).in("id",_leanIds).then(r=>({...r,_lean:true}),()=>({data:null,_lean:true})));
        if(_fullIds.length) _qs.push(client.from("listings").select(FEED_LISTING_SELECT).in("id",_fullIds).then(r=>({...r,_lean:false}),()=>({data:null,_lean:false})));
        return Promise.all(_qs).then(results=>{
          const freshById=new Map();
          results.forEach(r=>{if(Array.isArray(r.data))r.data.forEach(row=>{const it=r._lean?_mapSample(row):listingFromDB(row);if(it)freshById.set(String(it.id),it);});});
          return freshById;
        },()=>null);
      };
      //  l'accueil rebâti : l'ordre courant puis mes annonces hors de lui (profil/edit), chacune fraîche si relue, sinon celle du cache
      const construire=(ordre,mien,freshById,ech,vues=null)=>{
        if(reqId!==homeSampleReqRef.current) return;
        const seen=new Set(),merged=[];
        const push=id=>{if(seen.has(id))return;const it=(freshById&&freshById.get(id))||cachedById.get(id);if(it){merged.push(it);seen.add(id);}};
        ordre.forEach(push); mien.forEach(push);
        finalize(merged,ech,mien,vues);
      };
      //  L'ANCIENNE SONDE (le visiteur, un membre sans lieu, et le repli de fil_a_jour) : l'index id + updated_at, puis le diff
      const parSonde=()=>{
        const probe=lireEchantillon("id,updated_at");
        const mine=me?.id?client.from("listings").select("id,updated_at").eq("owner_id",me.id):Promise.resolve({data:[]});
        return Promise.all([probe.then(r=>r,()=>({data:null})),mine.then(r=>r,()=>({data:null}))]).then(([pr,mr])=>{
          if(reqId!==homeSampleReqRef.current) return;
          if(!Array.isArray(pr.data)) return; // sonde échouée → on garde l'état (cache déjà affiché) ; catchUp/prochain passage rattrape
          const order=pr.data, mineRows=Array.isArray(mr.data)?mr.data:[];
          const need=new Set();
          const mark=row=>{const cur=cachedById.get(String(row.id));const lms=cur&&cur.updatedAt?Date.parse(cur.updatedAt):NaN;const sms=Date.parse(row.updated_at||"");if(!cur||!Number.isFinite(lms)||(Number.isFinite(sms)&&sms>lms))need.add(String(row.id));};
          order.forEach(mark); mineRows.forEach(mark);
          const ids=[...need];
          if(ids.length>HOME_SAMPLE_SIZE*0.8) return fullFetch(); // GARDE-FOU never-increase : trop de périmées → full moins cher
          const mien=mineRows.map(r=>String(r.id));
          return relire(ids,new Set(mien)).then(fresh=>construire(order.map(r=>String(r.id)),mien,fresh,pr));
        });
      };
      //  (04/11, B-ACCUEIL-1) LE FIL À JOUR : une commande, et seulement ce qui a changé (bloc en tête de fichier)
      const appliquer=r=>{
        if(reqId!==homeSampleReqRef.current) return;
        const ids=[...r.o,...r.m].map(String);
        const need=besoinsDuFil(cachedById,ids,r.p);
        if(need.length>HOME_SAMPLE_SIZE*0.8) return fullFetch(); // GARDE-FOU never-increase, le même
        const vues=r.v?new Map(ids.map((id,i)=>[id,Number(r.v[i])||0])):null;
        const ech={data:r.o.map(id=>({id})),_fil:true,_sonde:r.sonde,_uid:r.uid};
        const mien=r.m.map(String);
        return relire(need,new Set(mien)).then(fresh=>construire(r.o.map(String),mien,fresh,ech,vues));
      };
      return filP.then(({fil,uid})=>{
        if(!fil||!uid) return parSonde();
        return filAJour(client,fil,uid,cachedById).then(r=>r?appliquer(r):parSonde());
      });
    }
    return fullFetch();
  },[me?.id,hydrateViewCounts]);
  // Feed-P2 : hydratation INSTANTANÉE depuis le cache warm-boot, AVANT le fetch réseau (rendu immédiat, 0
  //  spinner — la « rapidité »). Uniquement si `items` encore vide (ne JAMAIS écraser des données fraîches).
  //  Le fetch autoritaire (loadHomeSample, effet suivant) suit et corrige. Objets déjà en forme client (cache
  //  écrit post-listingFromDB) → setItems direct. Repli silencieux si cache absent/illisible.
  useEffect(()=>{
    if((itemsRef.current||[]).length) return; // clé GLOBALE (Feed-P5) → hydrate dès le boot, sans attendre me
    try{const c=JSON.parse(localStorage.getItem("lanac:cache:home")||"null");if(Array.isArray(c)&&c.length){setItems(c);c.forEach(it=>{ if(it&&it.id!=null&&Number.isFinite(it.filRang)) lanacFilRangs.set(String(it.id),it.filRang); });}}catch(e){}
  },[]);
  //  (04/11, B-ACCUEIL-1, revue d'architecte) LA LECTURE DE L'ACCUEIL EST TERMINÉE pour ce membre — aboutie OU échouée (hors ligne, sonde en
  //   échec) : la complétion des favoris (19-app) l'attend, sans jamais rester bloquée derrière une lecture qui n'a rien posé.
  const [homeTenteFor,setHomeTenteFor]=useState(undefined);
  useEffect(()=>{ const qui=me?.id||null, fin=()=>setHomeTenteFor(qui); Promise.resolve(loadHomeSample()).then(fin,fin); },[loadHomeSample]);
  //  (07/10) UN CHANGEMENT DE LIEU NE REDEMANDE L'ÉCHANTILLON QUE S'IL ÉTAIT COUPÉ. En dessous de 200 lignes, l'échantillon
  //   abouti EST toute la base visible : la réponse serait la même — aucune requête. Jamais deux fois pour le même lieu.
  lieuVerifRef.current=()=>{
    const d=derniereSondeRef.current; if(!d||!d.coupee) return;
    const s=sondeDuMembre(); if(!s) return;
    const sig=signatureDuFil(s);
    if(sig===d.sig||sig===sigDemandeeRef.current) return;
    loadHomeSample();
  };
  useEffect(()=>{ if(lieuVerifRef.current) lieuVerifRef.current(); },[_sigLieuFil]);
  //  L'interrupteur « Tenir compte de mes centres d'intérêt » : son état (par membre, sur cet appareil) et son geste. L'ordre
  //  vient du serveur : le changer, c'est RELIRE le fil (une lecture, la même qu'au démarrage — un geste rare, voulu).
  const [filPerso,setFilPerso]=useState(()=>filPersoOn(me?.id));
  const [filProfil,setFilProfil]=useState(null); // null : pas encore lu · {cles:[{k,p,n,i}], intention:[k]} (A39 : l'intention des 48 heures à part)
  const filProfilRef=useRef({uid:null,cles:null,lu:0,enVol:null});
  useEffect(()=>{ setFilPerso(filPersoOn(me?.id)); oublierInteretsDAvant(me?.id); setFilProfil(null); filProfilRef.current={uid:null,cles:null,lu:0,enVol:null}; },[me?.id]); // (13/10) un autre membre : le profil lu est oublié (jamais celui du précédent, même un instant)
  const reglerPersoDuFil=useCallback(on=>{
    const uid=me?.id; if(!uid) return;
    reglerFilPerso(uid,!!on); setFilPerso(!!on); filProfilRef.current={uid:null,cles:null,lu:0}; setFilProfil(null); loadHomeSample();
  },[me?.id,loadHomeSample]);
  //  « POURQUOI CET ORDRE ? » lit LE PROFIL du membre tel que la base le calcule (fil_profil) — À LA DEMANDE, quand la feuille
  //  s'ouvre, jamais au démarrage ; gardé pour la session (relu après 5 minutes, à l'interrupteur, ou pour un autre membre).
  const chargerFilProfil=useCallback(()=>{
    const uid=me?.id; if(!uid) return Promise.resolve(null);
    const f=filProfilRef.current;
    if(f.uid===uid&&f.cles&&Date.now()-f.lu<300000) return Promise.resolve(f.cles);
    if(f.enVol&&f.uid===uid) return f.enVol;
    const v=Promise.resolve(rpcFilProfil()).then(r=>{
      const d=(r&&!r.error&&r.data&&typeof r.data==="object")?r.data:{};
      const cles={cles:Array.isArray(d.cles)?d.cles.filter(c=>c&&typeof c.k==="string").map(c=>({k:c.k,p:Number(c.p)||0,n:Number(c.n)||0,i:Number(c.i)||0})):[],intention:Array.isArray(d.intention)?d.intention.filter(k=>typeof k==="string").slice(0,6):[]};
      filProfilRef.current={uid,cles,lu:Date.now(),enVol:null}; setFilProfil(cles); return cles;
    },()=>{ filProfilRef.current={uid,cles:null,lu:0,enVol:null}; return null; });
    filProfilRef.current={uid,cles:f.uid===uid?f.cles:null,lu:0,enVol:v};
    return v;
  },[me?.id]);
  // ═══ BLOC1-5 · les deux effets de CHARGEMENT du feed, déplacés VERBATIM ═══
  //   Fermeture VIDE prouvée par tools/app-closure.mjs : ils ne touchent que des
  //   états de CE hook + des imports + `me`. Placés ici, après tout ce qu'ils
  //   lisent (feedPaginationOn, hydrateViewCounts, itemsRef…), dans leur ordre
  //   relatif d'origine : voie paginée (WS6/WS8) puis sync delta (flag OFF).
  useEffect(()=>{
    const client=lanacSupabaseClient(); if(!client) return;
    let cancelled=false, countsTimer=null, chan=null;
    // Synchronisation DELTA : point « à jour jusqu'à » = plus grand updated_at SERVEUR vu passer
    // (event realtime ou ligne de catchUp) — jamais l'horloge du client (dérive = trous de synchro).
    // syncCursor garde la chaîne brute PostgREST (précision µs intacte pour .gt()), syncCursorMs sert
    // aux comparaisons. Ligne re-reçue à cheval sur une égalité de ms → simple upsert no-op, sans danger.
    // syncGapOpen = « il PEUT exister des évènements manqués derrière le curseur » : vrai au démarrage
    // et après toute coupure du canal ; seul un catchUp ABOUTI le referme. Tant qu'il est ouvert, les
    // events WS ne font PAS avancer le curseur (sinon il sauterait par-dessus l'évènement manqué et le
    // delta ne le rattraperait jamais). catchUpGen invalide la fermeture si une coupure survient pendant
    // le fetch de rattrapage lui-même.
    let syncCursor=null, syncCursorMs=null, syncGapOpen=true, catchUpGen=0;
    const advanceCursor=row=>{
      const raw=row&&row.updated_at; if(!raw) return;
      const ms=Date.parse(raw);
      if(Number.isFinite(ms)&&(syncCursorMs===null||ms>syncCursorMs)){syncCursorMs=ms;syncCursor=raw;}
    };
    const refreshCounts=()=>{if(countsTimer)clearTimeout(countsTimer);countsTimer=setTimeout(()=>{client.rpc("listings_category_counts").then(({data})=>{if(!cancelled&&data&&typeof data==="object")setCatCounts(data);},()=>{});},800);};
    const upsert=row=>{
      if(!syncGapOpen)advanceCursor(row); // event WS vu SANS trou derrière → avance le point de synchro (même no-op « vues seules »)
      const it=listingFromDB(row); if(!it)return false;
      // Court-circuit anti-egress : si l'annonce déjà en mémoire ne diffère QUE par son compteur de vues
      // (event realtime de increment_listing_views, rediffusé à tous les clients), on ne fait RIEN — aucune
      // ref recréée, aucun re-render, et l'appelant saura ne pas relancer refreshCounts (voir handlers).
      // Le tic « +1 vue » du viewer est déjà appliqué localement/optimistiquement à l'ouverture de fiche.
      // Lecture via itemsRef.current (ref de rendu, toujours à jour) : décision fiable AVANT tout setState.
      const cur=(itemsRef.current||[]).find(x=>String(x.id)===String(it.id));
      if(cur&&listingEqualExceptViews(cur,it)) return false;
      // Vues = compteur MONOTONE. Depuis que la source des vues est listing_views (RPC listing_view_counts +
      // listings_feed) et non plus le temps-réel, le `it.views` porté par un event realtime provient de
      // listings.views (colonne désormais figée) : il ne doit JAMAIS faire régresser une valeur déjà hydratée
      // plus fraîche (ex. un event de changement de PRIX ne doit pas ramener 35 vues à 28). On conserve donc le
      // max par collection. Avant ce chantier c'était un no-op (les deux sources coïncidaient).
      const maxViews=(a,b)=>Math.max(Number(a||0),Number(b||0));
      setItems(prev=>{const m=new Map(prev.map(x=>[String(x.id),x]));const p=m.get(String(it.id));m.set(String(it.id),p?{...it,views:maxViews(it.views,p.views),...(p.filRang!=null?{filRang:p.filRang}:{})}:it);return [...m.values()].sort((a,b)=>Number(b.ts||0)-Number(a.ts||0));});
      const see=canSeeListingRef.current?canSeeListingRef.current(it):true;
      setFeedItems(prev=>{const has=prev.some(x=>String(x.id)===String(it.id));if(!has)return prev;return see?prev.map(x=>String(x.id)===String(it.id)?{...it,views:maxViews(it.views,x.views)}:x):prev.filter(x=>String(x.id)!==String(it.id));});
      // La fiche/carte ouverte (detail) suit le statut serveur en direct (RÉSERVÉ/VENDU…) : on FUSIONNE la ligne
      // reçue dans `sel` (et non remplacement) pour préserver d'éventuels champs UI locaux + le compteur de vues
      // monotone. Libellés de statut traduits par le rendu (i18n).
      setSel(prev=>prev&&String(prev.id)===String(it.id)?{...prev,...it,views:maxViews(it.views,prev.views)}:prev);
      return true;
    };
    const del=id=>{if(id==null)return;setItems(prev=>prev.filter(x=>String(x.id)!==String(id)));setFeedItems(prev=>prev.filter(x=>String(x.id)!==String(id)));};
    // Rattrapage realtime : re-fetch ciblé des annonces VISIBLES (fiche ouverte + cartes de la grille) pour
    // combler tout évènement manqué pendant une coupure/arrière-plan. `upsert` met à jour items/feedItems/sel
    // EN PLACE → ni l'ordre, ni le curseur, ni le scroll de la pagination ne changent. Egress borné (≤300 ids,
    // uniquement sur reconnexion/retour au 1er plan — évènements rares). Une grille/fiche vide = no-op.
    // Plafond dur anti-tempête : au plus UN rattrapage réseau toutes les 15 s, quoi qu'il arrive
    // (flapping WS, rafales SUBSCRIBED/visibilitychange). Un appel arrivé trop tôt n'est PAS perdu :
    // il est REPROGRAMMÉ à la fin de la fenêtre (trailing) — indispensable, car tant que le rattrapage
    // n'a pas tourné, le trou de synchro (syncGapOpen) reste ouvert. Les events realtime normaux, eux,
    // passent sans aucune limite : seul le refetch REST est borné.
    const CATCHUP_MIN_INTERVAL_MS=15000;
    let lastCatchUpAt=0, catchUpTrailer=null;
    const catchUp=()=>{
      if(cancelled) return;
      if(!lanacUidRange()) return; // (B-ACCUEIL-5) sans session, rien à rattraper : l'écran de connexion ne montre aucune annonce
      const wait=lastCatchUpAt+CATCHUP_MIN_INTERVAL_MS-Date.now();
      if(wait>0){
        if(!catchUpTrailer)catchUpTrailer=setTimeout(()=>{catchUpTrailer=null;catchUp();},wait);
        return;
      }
      const ids=new Set();
      // Priorité aux cartes certainement visibles (fiche ouverte + grille marché), puis un échantillon des
      // annonces chargées qui alimentent l'accueil (rails / Vu récemment / favoris en rendent un sous-ensemble).
      const open=selRef.current; if(open?.id!=null) ids.add(String(open.id));
      (feedItemsRef.current||[]).forEach(x=>{if(x?.id!=null) ids.add(String(x.id));});
      (itemsRef.current||[]).slice(0,200).forEach(x=>{if(x?.id!=null) ids.add(String(x.id));});
      const list=[...ids].slice(0,300);
      if(!list.length){syncGapOpen=false;return;} // rien d'affiché → rien à rattraper, le direct (INSERT/UPDATE) suffit
      lastCatchUpAt=Date.now();
      // DELTA : après le 1er rattrapage complet de la session, on ne demande que ce qui a bougé depuis
      // le dernier point de synchro → « rien n'a changé » = 0 ligne au lieu de ~150 lignes complètes.
      // select("*") conservé → jeu de champs STRICTEMENT identique à avant (cœur, statut, prix, photos…),
      // seul le NOMBRE de lignes renvoyées change. Curseur = updated_at serveur (trigger set_updated_at).
      const myGen=catchUpGen;
      if(syncCursor){
        // DELTA (inchangé) : seules les lignes modifiées depuis le point de synchro reviennent — « rien
        // n'a bougé » = réponse []. select("*") conservé → jeu de champs strictement identique.
        client.from("listings").select(FEED_LISTING_SELECT).in("id",list).gt("updated_at",syncCursor).then(({data})=>{
          if(cancelled)return;
          if(Array.isArray(data)){data.forEach(advanceCursor);data.forEach(upsert);}
          // Trou refermé SEULEMENT si aucune coupure n'est survenue pendant ce fetch (sinon un évènement
          // a pu se glisser entre la réponse et la reconnexion → le prochain catchUp le couvrira).
          if(myGen===catchUpGen&&Array.isArray(data))syncGapOpen=false;
        },()=>{}); // échec réseau → syncGapOpen reste ouvert, le prochain passage rattrape tout
        return;
      }
      // Fix A egress (11/07) — premier rattrapage SANS curseur (boot/reload) : l'ancien « select * des
      // ~150 ids » re-téléchargeait ~254 KB de lignes que le boot venait de charger (feed/échantillon).
      // À la place : (1) SONDE id+updated_at (~5 KB) ; (2) comparaison au local (items/feedItems/sel
      // portent updatedAt via listingFromDB) ; (3) re-fetch complet des SEULES lignes périmées, via le
      // MÊME upsert (mêmes champs, même court-circuit vues, même protection du compteur de favoris).
      // Invariants préservés : une ligne absente de la réponse n'est PAS retirée (l'ancien full fetch ne
      // le faisait pas non plus) ; le curseur n'est posé et le trou refermé QU'APRÈS succès complet —
      // si le fetch des périmées échoue, rien n'avance et la sonde entière sera rejouée (aucune ligne
      // ne peut rester périmée derrière un curseur déjà avancé). Comparaison en ms (Date.parse) : un
      // écart sous la milliseconde est couvert par l'event WS ou le prochain delta. Item local sans
      // updatedAt (vieux cache / ancien bundle) → traité comme périmé (sûr, converge en un fetch).
      client.from("listings").select("id,updated_at").in("id",list).then(({data})=>{
        if(cancelled||!Array.isArray(data))return; // échec → trou ouvert, la sonde sera rejouée
        const local=new Map();
        (itemsRef.current||[]).forEach(x=>{if(x?.id!=null)local.set(String(x.id),x);});
        (feedItemsRef.current||[]).forEach(x=>{if(x?.id!=null)local.set(String(x.id),x);});
        const open=selRef.current; if(open?.id!=null)local.set(String(open.id),open);
        const staleIds=[];
        data.forEach(row=>{
          const cur=local.get(String(row.id));
          const localMs=cur&&cur.updatedAt?Date.parse(cur.updatedAt):NaN;
          const serverMs=Date.parse(row.updated_at||"");
          if(!cur||!Number.isFinite(localMs)||(Number.isFinite(serverMs)&&serverMs>localMs))staleIds.push(row.id);
        });
        const seal=()=>{data.forEach(advanceCursor);if(myGen===catchUpGen)syncGapOpen=false;};
        if(!staleIds.length){seal();return;}
        client.from("listings").select(FEED_LISTING_SELECT).in("id",staleIds).then(({data:rows})=>{
          if(cancelled)return;
          if(!Array.isArray(rows))return; // échec → ni curseur ni fermeture : sonde rejouée au prochain passage
          rows.forEach(upsert);
          rows.forEach(advanceCursor); // idempotent (max) — couvre le cas d'une ligne re-bumpée entre sonde et fetch
          seal();
        },()=>{});
      },()=>{}); // échec réseau → syncGapOpen reste ouvert, le prochain passage rattrape tout
    };
    let reconnectTimer=null, reconnectAttempts=0, stableTimer=null;
    // Résilience alignée sur le canal global (messages) : jeton frais AVANT chaque (re)connexion (sinon, après
    // rotation du JWT, auth.uid() devient null → postgres_changes RLS ne livre plus, en silence), puis backoff.
    const createChannel=async()=>{
      if(cancelled)return;
      // Plus de canal GLOBAL listings (postgres_changes retiré, publication realtime vide) : la fraîcheur de
      // la grille = navigation (loadFeed/loadHomeSample/pull-to-refresh) + rattrapage REST borné au retour au
      // 1er plan (catchUp, ici + via onVisible) ; détail ouvert = Broadcast SCOPÉ lanac:listing:<id>.
      catchUp();
    };
    createChannel();
    const onVisible=()=>{
      if(document.visibilityState==="visible"&&!cancelled){
        // getSession() déclenche le refresh du JWT si nécessaire (lock interne Supabase, pas de conflit de rotation).
        client.auth.getSession().catch(()=>{});
        if(!chan||chan.state==="closed"||chan.state==="errored"){reconnectAttempts=0;createChannel();}
        else catchUp();
      }
    };
    document.addEventListener("visibilitychange",onVisible);
    // A36 · 2c-1 (11/10) : la RELECTURE demandée par la réconciliation à l'échéance d'une réservation en mémoire — le même
    //  rattrapage borné (delta depuis le curseur, 15 s au moins entre deux) : l'écran suit ce que le serveur a écrit, sans
    //  qu'aucun appareil n'anticipe plus l'échéance.
    const onRelire=()=>{if(!cancelled) catchUp();};
    window.addEventListener("lanac:relire-annonces",onRelire);
    // A36 · 2b : au retour d'une absence plus longue que la fenêtre de diffusion du serveur, la grille se relit (delta borné)
    const onPresent=(ev)=>{ try{ if(!cancelled&&Number(ev&&ev.detail&&ev.detail.absence)>=PRESENCE_LONGUE_MS) catchUp(); }catch(e){} };
    window.addEventListener("lanac:present",onPresent);
    return()=>{cancelled=true;document.removeEventListener("visibilitychange",onVisible);window.removeEventListener("lanac:relire-annonces",onRelire);window.removeEventListener("lanac:present",onPresent);if(countsTimer)clearTimeout(countsTimer);if(reconnectTimer)clearTimeout(reconnectTimer);if(stableTimer)clearTimeout(stableTimer);if(catchUpTrailer)clearTimeout(catchUpTrailer);const c=chan;chan=null;if(c){try{client.removeChannel(c);}catch(e){}}};
  },[]);
  // ═══ EGRESS B4 (01/08/2026) · LE CHEMIN LEGACY O(N) EST SUPPRIMÉ, PAS DÉSACTIVÉ ═══
  //  Ici vivait le dernier code O(taille de la plateforme) : `lanac:feedPagination="0"`
  //  déclenchait un `fetchTableDelta("listings", …)` de la table ENTIÈRE (cache `lanac:items`).
  //  À 2 000 annonces c'était déjà ~2 Mo décodés par appareil ; à 10 000, la page d'accueil
  //  d'un kill-switch « de secours » aurait coûté plus cher que la panne qu'il couvrait.
  //  POURQUOI SUPPRIMER PLUTÔT QUE LAISSER DORMIR — trois faits, pas une préférence :
  //   1. le chemin serveur est le DÉFAUT PROD depuis WS8 (contrat 3a) et n'a jamais dû être
  //      éteint ; le kill-switch n'a servi qu'aux bancs, qui ont leurs propres overrides ;
  //   2. ce chemin n'avait AUCUN filet e2e (contrat 3a : « seul chemin O(N) restant, sans
  //      filet ») — un repli jamais exercé n'est pas un repli, c'est une croyance ;
  //   3. le cliquet du banc est ARMÉ (B3) : toute régression d'egress qui tenterait de
  //      revenir par ici mordra à la mesure. La suppression se fait SOUS le mur, pas avant.
  //  HYGIÈNE : on retire le cache legacy des appareils qui l'ont encore (poids mort à ~2 Mo).
  useEffect(()=>{
    try{localStorage.removeItem(CACHE_ITEMS_KEY);localStorage.removeItem(CACHE_ITEMS_SYNC_KEY);localStorage.removeItem("lanac:feedPagination");}catch(e){}
  },[]);
  // ═══ BLOC2-1 · effets multi-hooks accueillis ici via le contrat `ports` ═══
  //   Fermeture VIDE prouvée par app-closure : ils ne touchent que des états de
  //   CE hook + des ports (view, lang, setViewedHistory) + des imports + `me`.
  //  (04/11, B-ACCUEIL-5) les compteurs par catégorie : jamais sans session (l'écran de connexion), relus quand elle apparaît (la connexion).
  const _sessionLa=!!me?.id||!!lanacUidRange();
  useEffect(()=>{
    const client=lanacSupabaseClient(); if(!client||!_sessionLa) return;
    let cancelled=false;
    client.rpc("listings_category_counts").then(({data})=>{if(!cancelled&&data&&typeof data==="object")setCatCounts(data);},()=>{});
    return()=>{cancelled=true;};
  },[view,_sessionLa]);
  useEffect(()=>{
    const q=(topSearch||"").trim();
    if(!q){setSearchSuggestItems([]);return;}
    const client=lanacSupabaseClient(); if(!client) return;
    let cancelled=false;
    const timer=setTimeout(()=>{
      client.rpc("listings_search_suggest",{p_q:q,p_lang:lang,p_limit:6}).then(({data})=>{if(!cancelled&&Array.isArray(data))setSearchSuggestItems(data);},()=>{});
    },220);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[topSearch,lang]);
  //  (05/10/2026, A20 lot 1) NOTER UNE VUE — UNE règle, DEUX portes. La fiche qui s'ouvre (l'effet ci-dessous, inchangé) et le
  //   plein écran du fil vivant (noterVueAnnonce : 2 s d'arrêt ou un geste, décidé par lui). Le corps est celui d'avant, mot
  //   pour mot, avec l'identifiant en argument. Quand le plein écran vient de noter une annonce et que le membre ouvre sa
  //   fiche (« Détails »), la fiche ne la note PAS une seconde fois (10 min) : une visite, une vue, deux écritures — pas quatre.
  const vuesDuPleinEcranRef=useRef(new Map());   // id → instant : notées par le plein écran, pas encore reprises par la fiche
  useEffect(()=>{ vuesDuPleinEcranRef.current.clear(); },[me?.id]);
  const noterVue=id=>{
    setViewedHistory(p=>[{id,ts:Date.now()},...p.filter(x=>x.id!==id)].slice(0,20));
    const markViewer=i=>({userId:me.id,ts:Date.now()});
    setItems(p=>p.map(i=>i.id===id?{...i,views:Number(i.views||0)+1,recentlyViewed:true,recentViewers:[markViewer(i),...(i.recentViewers||[]).filter(v=>v.userId!==me.id)].slice(0,40)}:i));
    setSel(prev=>prev&&prev.id===id?{...prev,views:Number(prev.views||0)+1,recentlyViewed:true,recentViewers:[markViewer(prev),...(prev.recentViewers||[]).filter(v=>v.userId!==me.id)].slice(0,40)}:prev);
    // Persiste la vue côté serveur (compteur partagé entre tous les users)
    // NB: .rpc() n'est pas patché par lanacSupabaseClient → pas de .catch, on utilise .then(undefined,…) avec garde
    // P2 · Le serveur ne compte plus qu'UNE vue par spectateur et par jour (déduplication par
    //   `auth.uid()`, infalsifiable). Le `+1` optimiste ci-dessus resterait donc faux à la 2e
    //   ouverture du jour : on RECALE sur la valeur que le serveur renvoie. L'écran affiche ce
    //   que la base contient, jamais une estimation qu'elle dément.
    try{const _vc=lanacSupabaseClient();if(_vc&&typeof _vc.rpc==="function"){const _q=rpcIncrementListingViews(id);if(_q&&typeof _q.then==="function")_q.then(r=>{
      const _n=typeof r==="number"?r:(r&&typeof r.data==="number"?r.data:null);
      if(_n===null||!Number.isFinite(_n))return;                       // pas de valeur exploitable → on garde l'optimiste
      setItems(p=>p.map(i=>i.id===id?{...i,views:_n}:i));
      setSel(prev=>prev&&prev.id===id?{...prev,views:_n}:prev);
    },()=>{});}}catch(_){} // TECH-RPC-1 : passerelle
    // Persiste "Vu récemment" côté serveur (synchro multi-appareils) — best-effort, ne bloque jamais l'ouverture
    //  (30/10/2026, lot D · la sentinelle) par lanacWrite : l'« upsert » avalait son erreur (`.catch` n'attrape rien : supabase-js résout
    //   avec {error}) ; un refus se DIT (write_failed) ; une coupure reste muette (quiet offline : chaque ouverture hors ligne ne coûte pas un rapport).
    if(lanacSupabaseClient()) lanacWrite({what:"vu récemment",table:"recently_viewed",op:"upsert",values:{user_id:me.id,listing_id:id,ts:Date.now()},onConflict:"user_id,listing_id",columns:"listing_id",expect:"one",quiet:["offline"]});
  };
  useEffect(()=>{
    if(!me||!sel?.id) return;
    if(sel.ownerId===me.id) return;
    const deja=vuesDuPleinEcranRef.current.get(sel.id);
    if(deja){ vuesDuPleinEcranRef.current.delete(sel.id); if(Date.now()-deja<600000) return; }   // le plein écran vient de la noter
    noterVue(sel.id);
  },[sel?.id,me?.id]);
  //  La porte du plein écran. Mêmes gardes que la fiche (un membre connecté, jamais sa propre annonce). Rend true si la vue est notée.
  const noterVueAnnonce=annonce=>{
    if(!me||!annonce||annonce.id==null||annonce.ownerId===me.id) return false;
    vuesDuPleinEcranRef.current.set(annonce.id,Date.now());
    noterVue(annonce.id);
    return true;
  };
  // BLOC4 · overlay de recherche mobile (classe body + focus du champ).
  //   `mobileSearchInputRef` est RETOURNÉ : App le pose sur l'<input> en JSX.
  const mobileSearchInputRef=useRef(null);
  useEffect(()=>{
    const open=isMobile&&(view==="home"||view==="market")&&searchFocused&&!sel;
    document.body.classList.toggle("lanac-mobile-search-open",open);
    if(open) requestAnimationFrame(()=>mobileSearchInputRef.current?.focus({preventScroll:true}));
    return()=>document.body.classList.remove("lanac-mobile-search-open");
  },[isMobile,view,searchFocused,sel]);
  // ═══ Mécanique PULL-TO-REFRESH (déplacée VERBATIM depuis App()) ═══════════
  //  attachPullToRefresh + ses 2 refs. RETOURNÉS : ptrBarRef (JSX), gestureLockRef
  //  (swipe-back d'App), attachPullToRefresh (les 2 effets PTR d'App). Les setters
  //  PTR qu'il pilote (setPtrRefreshing/SkClosing/SkTop) vivent déjà ici.
  const ptrBarRef=useRef(null); // Pull-to-refresh : jauge fine (position:fixed, hors des zones translatées)
  const gestureLockRef=useRef(null); // exclusivité des gestes : pull-to-refresh ↔ swipe-back (jamais les deux)
  const attachPullToRefresh=useCallback((cfg)=>{
    const bar=ptrBarRef.current; if(!bar) return ()=>{};
    const fill=bar.querySelector(".lnc-ptr-bar-fill");
    const THRESH=64, MAXP=92, DAMP=0.55, MINSHOW=650;
    const reduce=!!(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    let startY=0, tracking=false, engaged=false, pull=0, busy=false, tel=null, restTop=64, skTop=64, clearTimer=null, closeTimer=null;
    const sheetOpen=()=>document.body.classList.contains("lnc-sheet-lock");
    const atTop=()=>cfg.scrollTopOf()<=2;
    const canTranslate=()=>cfg.translate&&tel&&!reduce;
    const setVisual=(p)=>{
      pull=p; const prog=Math.min(1,p/THRESH);
      if(canTranslate()) tel.style.transform="translateY("+p.toFixed(1)+"px)";
      bar.style.opacity=String(Math.min(1,p/12));
      if(fill) fill.style.width=(prog*100).toFixed(1)+"%";
      bar.classList.toggle("lnc-ptr-bar--armed",p>=THRESH);
    };
    const resetTel=(animate)=>{
      if(clearTimer){clearTimeout(clearTimer);clearTimer=null;}
      if(cfg.translate&&tel){ tel.style.transition=animate?"transform .4s cubic-bezier(.34,1.22,.4,1)":"none"; tel.style.transform=reduce?"":"translateY(0px)"; const el=tel; clearTimer=setTimeout(()=>{ if(!busy&&el){el.style.transform="";el.style.transition="";} },animate?440:0); }
    };
    const hideBar=()=>{ bar.style.transition="opacity .3s"; bar.style.opacity="0"; bar.classList.remove("lnc-ptr-bar--armed"); if(fill)fill.style.width="0%"; };
    const snapBack=()=>{ resetTel(true); hideBar(); pull=0; engaged=false; if(gestureLockRef.current==="ptr")gestureLockRef.current=null; };
    const startRefresh=()=>{
      busy=true;
      if(gestureLockRef.current==="ptr")gestureLockRef.current=null; // geste terminé (le refresh n'est pas un geste)
      try{navigator.vibrate&&navigator.vibrate(8);}catch(_){}
      setPtrSkClosing(false); setPtrSkTop(skTop); setPtrRefreshing(true); // overlay squelette (React)
      if(fill){ fill.style.transition="width .18s"; fill.style.width="100%"; }
      resetTel(true);            // le contenu revient à sa place ; le squelette (fixe) couvre le haut
      setTimeout(hideBar,180);
      const started=Date.now();
      Promise.resolve(cfg.refresh()).catch(()=>{}).then(()=>{
        const wait=Math.max(0,MINSHOW-(Date.now()-started));
        if(closeTimer)clearTimeout(closeTimer);
        closeTimer=setTimeout(()=>{
          setPtrSkClosing(true); // fondu de sortie sur le contenu frais
          setTimeout(()=>{ if(cfg.translate&&tel){tel.style.transform="";tel.style.transition="";} busy=false; setPtrRefreshing(false); setPtrSkClosing(false); },300);
        },wait);
      });
    };
    const onStart=(e)=>{
      if(busy||!atTop()||sheetOpen()) return;
      const t=e.touches&&e.touches[0]; if(!t) return;
      tel=cfg.readyEl(); if(!tel) return; // surface non prête (ex. fil ouvert dans la messagerie) → n'engage pas
      startY=t.clientY; tracking=true; engaged=false;
      restTop=Math.max(0,tel.getBoundingClientRect().top);
      skTop=cfg.skTopOf?Math.max(0,cfg.skTopOf()):restTop; // position de l'overlay squelette (peut différer du haut de contenu)
      // Jauge : même ancrage mesuré que le squelette (sous les barres collantes) — sinon elle se place
      // DERRIÈRE une barre sticky (z-index supérieur) sur les pages à barre de titre. skTop-2 = bas du chrome.
      bar.style.top=(cfg.skTopOf?Math.max(0,skTop-2):restTop)+"px";
    };
    const onMove=(e)=>{
      if(!tracking||busy) return;
      const t=e.touches&&e.touches[0]; if(!t) return;
      const dy=t.clientY-startY;
      if(!atTop()||sheetOpen()){ if(engaged){engaged=false;snapBack();} tracking=false; return; }
      if(dy>0&&(engaged||dy>6)){
        if(!engaged){
          if(gestureLockRef.current&&gestureLockRef.current!=="ptr"){tracking=false;return;} // swipe-back en cours → on n'engage pas
          gestureLockRef.current="ptr";
          engaged=true; if(canTranslate())tel.style.transition="none"; bar.style.transition="none"; try{navigator.vibrate&&navigator.vibrate(3);}catch(_){}
        }
        if(e.cancelable) e.preventDefault(); // n'intercepte QUE pendant un tirage engagé
        const wasArmed=pull>=THRESH;
        setVisual(Math.min(MAXP,dy*DAMP));
        if(!wasArmed&&pull>=THRESH){try{navigator.vibrate&&navigator.vibrate(10);}catch(_){}}
      } else if(engaged){ engaged=false; snapBack(); }
    };
    const onEnd=()=>{
      if(!tracking) return; tracking=false;
      if(!engaged) return; engaged=false;
      if(pull>=THRESH) startRefresh(); else snapBack();
    };
    window.addEventListener("touchstart",onStart,{passive:true});
    window.addEventListener("touchmove",onMove,{passive:false});
    window.addEventListener("touchend",onEnd,{passive:true});
    window.addEventListener("touchcancel",onEnd,{passive:true});
    return ()=>{
      window.removeEventListener("touchstart",onStart);
      window.removeEventListener("touchmove",onMove);
      window.removeEventListener("touchend",onEnd);
      window.removeEventListener("touchcancel",onEnd);
      if(clearTimer)clearTimeout(clearTimer);
      if(closeTimer)clearTimeout(closeTimer);
      if(cfg.translate&&tel){tel.style.transform="";tel.style.transition="";}
      if(bar){bar.style.opacity="0";bar.classList.remove("lnc-ptr-bar--armed");}
      if(gestureLockRef.current==="ptr")gestureLockRef.current=null;
    };
  },[setPtrRefreshing,setPtrSkClosing,setPtrSkTop]);
  // ───────────────────────────────────────────────────────────────────────────
  // B — Annonces mobile : pile d'undo des FILTRES pour la flèche retour.
  // Réactif (un seul point d'observation), zéro instrumentation des setters.
  // Cloisonné : agit uniquement sur mobile + view==="market". N'altère JAMAIS le feed/
  // pagination (restaurer = un simple changement de filtres, déjà géré par listingFilterSig).
  // (migré verbatim depuis App() le 16/07/2026 — filet : e2e S18)
  // ───────────────────────────────────────────────────────────────────────────
  const filterSnapRef=useRef(null);       // dernier état de filtres connu
  const filterHistRef=useRef([]);         // pile des états précédents (intra-marché)
  const filterRestoringRef=useRef(false); // garde anti-boucle pendant une restauration
  const filterPrevViewRef=useRef(null);   // vue précédente (entrée marché vs retour de feuille de filtres)
  useEffect(()=>{
    if(!isMobile){filterPrevViewRef.current=view;return;}
    const snap={filterCat,filterAdType,filterOther,filterOtherSub,filterCountry,filterCity,radiusKm,filterCoords,srch,maxP,filterStatus,filterRooms,filterPropType,filterBrand,filterFuel,filterWorkType};
    const prevView=filterPrevViewRef.current;
    filterPrevViewRef.current=view;
    if(view==="search") return;                                                   // feuille de filtres : on conserve pile + snapshot pré-feuille
    if(view!=="market"){filterHistRef.current=[];filterSnapRef.current=snap;return;} // vraie page hors marché : pile vidée
    if(sel){filterSnapRef.current=snap;return;}                                    // détail ouvert : ne pas empiler
    const fromSheet=prevView==="search"&&mobileFilterReturn==="market";
    if(prevView!=="market"&&!fromSheet){filterHistRef.current=[];filterSnapRef.current=snap;return;} // entrée fraîche : état de base
    if(filterRestoringRef.current){filterRestoringRef.current=false;filterSnapRef.current=snap;return;} // restauration : pas de ré-empilement
    const prev=filterSnapRef.current;
    if(prev&&JSON.stringify(prev)!==JSON.stringify(snap)) filterHistRef.current=[prev,...filterHistRef.current].slice(0,20);
    filterSnapRef.current=snap;
  },[isMobile,view,sel,mobileFilterReturn,filterCat,filterAdType,filterOther,filterOtherSub,filterCountry,filterCity,radiusKm,filterCoords,srch,maxP,filterStatus,filterRooms,filterPropType,filterBrand,filterFuel,filterWorkType]);
  const restorePrevFilters=()=>{
    const hist=filterHistRef.current;
    if(!hist||!hist.length) return false;
    const prev=hist[0];
    filterHistRef.current=hist.slice(1);
    filterRestoringRef.current=true;
    setFilterCat(prev.filterCat);setFilterAdType(prev.filterAdType);setFilterOther(prev.filterOther);setFilterOtherSub(prev.filterOtherSub);
    setFilterCountry(prev.filterCountry);setFilterCity(prev.filterCity);setRadiusKm(prev.radiusKm);setFilterCoords(prev.filterCoords??null);setSrch(prev.srch);setMaxP(prev.maxP);
    setFilterStatus(prev.filterStatus);setFilterRooms(prev.filterRooms);setFilterPropType(prev.filterPropType);setFilterBrand(prev.filterBrand);setFilterFuel(prev.filterFuel);setFilterWorkType(prev.filterWorkType);
    return true;
  };
  // Remet TOUS les filtres du marché à leur valeur par défaut (utilisé par "Voir tout" et au retour hors marché).
  const resetMarketFilters=()=>{
    setFilterCat(null);setFilterAdType(null);setFilterOther(null);setFilterOtherSub("");
    setFilterCountry("all");setFilterCity("all");setRadiusKm(0);setFilterCoords(null);setSrch("");setMaxP(5000000);
    setFilterStatus("all");setFilterRooms(null);setFilterPropType("");setFilterBrand("");setFilterFuel("");setFilterWorkType("");
    //  (02/10, lot B) le mode « position » n'est plus éteint ici : la position est le LIEU du membre, pas un filtre du marché.
    //  Avant, « Voir tout » la coupait à moitié (mode éteint, position et clé gardées) — elle revenait au rechargement suivant.
  };
  // ===== Sous-système FEED PAGINÉ (WS6) — migré VERBATIM depuis App() le 17/07/2026 =====
  //  Étape 2/2 du sous-système feed (l'étape 1 — mécanique du pull-to-refresh — a été livrée
  //  le 16/07). Elle attendait son filet : e2e S19 (marché paginé, fixtures e2e-seed), posé et
  //  validé sur le code d'AVANT cette migration. Le port `setAllUsers` est fourni par
  //  useAdminData, appelé AVANT useFeedState — aucun réordonnancement de hooks.
  // ===== WS6 — Feed paginé serveur (flag OFF par défaut → comportement actuel STRICTEMENT inchangé) =====
  const feedCursorRef=useRef(null);
  const feedReqRef=useRef(0);
  const feedCountReqRef=useRef(0); // garde anti-réponse-périmée pour listings_feed_count (comme feedReqRef pour la grille)
  const facetsReqRef=useRef(0);            // garde anti-réponse-périmée pour listings_facets
  const facetsCacheRef=useRef(new Map());  // signature -> facettes (cache MÉMOIRE, borné à la session ; réponse ~200 o)
  const feedKnownSellersRef=useRef(new Set());
  const mergeFeedSellers=useCallback((rows,enDouceur=false)=>{
    try{
      const client=lanacSupabaseClient(); if(!client) return;
      const ids=[...new Set((rows||[]).map(it=>it.ownerId).filter(Boolean))].filter(id=>!feedKnownSellersRef.current.has(id));
      if(!ids.length) return;
      ids.forEach(id=>feedKnownSellersRef.current.add(id));
      client.from("profiles_secure").select(FEED_PROFILE_COLS).in("id",ids).then(({data})=>{
        if(Array.isArray(data)&&data.length){const mapped=data.map(profileFromDB).filter(Boolean);const poser=()=>setAllUsers(prev=>{const byId=new Map(prev.map(u=>[String(u.id),u]));mapped.forEach(u=>byId.set(String(u.id),u));return [...byId.values()];});if(enDouceur&&typeof React.startTransition==="function") React.startTransition(poser); else poser();}
      },()=>{});
    }catch(e){}
  },[]);
  // ── Feed Ph2 (C3) · CACHE PERSISTANT de la PAGE 1 du marché — stale-while-revalidate ──
  //  L'accueil a son warm-boot delta (Feed-P2/P3) mais la grille MARCHÉ re-fetchait page 1
  //  à CHAQUE entrée, écran vide (squelettes) pendant le réseau. On persiste la dernière
  //  page 1 par SIGNATURE DE FILTRE : à l'entrée, si la signature correspond, les cartes
  //  s'affichent INSTANTANÉMENT depuis le cache pendant que loadFeed(true) rafraîchit et
  //  REMPLACE (reset) — jamais de donnée périmée à l'écran plus de quelques centaines de ms.
  //  ⭐ BORNÉ : UNE seule clé par utilisateur (la dernière signature), ~24 items maigres
  //  ≈ 30 Ko — pas d'accumulation par signature. ⭐ ZÉRO egress ajouté : loadFeed part
  //  toujours ; on ne fait que peindre plus tôt. ⭐ Pendant l'hydratation, hasMore=false ⇒
  //  la sentinelle est absente ⇒ aucun loadMore ne peut partir avec un curseur périmé.
  //  Kill-switch : localStorage lanac:marketPageCache="0" (aucune lecture/écriture).
  const marketPageCacheOn=(()=>{try{return localStorage.getItem("lanac:marketPageCache")!=="0";}catch(e){return true;}})();
  const MARKET_CACHE_KEY=()=>`lanac:cache:market:${me?.id||"anon"}`;
  const feedSigRef=useRef(null); // signature courante, lue par loadFeed à l'écriture du cache (pas de closure périmée)
  //  (06/10) `enDouceur` — la page suivante demandée par le PLEIN ÉCRAN ouvert depuis les résultats : il couvre la grille, on y
  //  glisse. Posée d'un bloc, elle re-rendait l'application en deux tâches de ~350 ms (processeur ralenti 4 fois, 24 → 48
  //  cartes) pendant le geste. Elle est posée en TRANSITION : React la rend par petits pas et rend la main au doigt entre deux.
  //  La sentinelle de la grille n'en demande pas : son chemin est celui d'avant.
  //  UN SEUL AJOUT DE PAGE À LA FOIS — garde SYNCHRONE : en douceur, « chargement » n'est posé qu'en transition, l'état ne garde
  //  donc plus la porte tout de suite ; deux ajouts partis ensemble liraient le même curseur et poseraient la même page deux
  //  fois. Un rechargement (reset) reprend la main : l'ajout en vol sera ignoré à son retour (reqId).
  const feedAjoutEnVolRef=useRef(false);
  const loadFeed=useCallback((reset,enDouceur=false)=>{
    const client=lanacSupabaseClient(); if(!client) return Promise.resolve();
    const pr=feedParamsRef.current; if(!pr||!pr.params) return Promise.resolve();
    if(!reset){ if(feedAjoutEnVolRef.current) return Promise.resolve(); feedAjoutEnVolRef.current=true; } else feedAjoutEnVolRef.current=false;
    const reqId=++feedReqRef.current;
    const doux=f=>{ if(enDouceur&&typeof React.startTransition==="function") React.startTransition(f); else f(); };
    //  en douceur, RIEN n'est posé au départ (un rendu de l'application de moins pendant le geste) : la grille est couverte, personne
    //  ne lit « chargement », et la porte est gardée par la référence ci-dessus
    if(!enDouceur){setFeedLoading(true);setFeedError(false);}
    const cur=reset?{}:(feedCursorRef.current||{});
    return client.rpc("listings_feed",{...pr.params,...cur,p_sort:pr.sort,p_limit:LISTING_PAGE_SIZE}).then(({data,error})=>{
      if(reqId!==feedReqRef.current) return; // réponse périmée (filtres changés entre-temps)
      if(!reset) feedAjoutEnVolRef.current=false;
      if(error){console.error("[LANAC] feed error:",error.message);setFeedError(true);setFeedLoading(false);return;}
      const rows=Array.isArray(data)?data:[];
      const items=rows.map(r=>listingFromDB(r.item)).filter(Boolean);
      mergeFeedSellers(items,enDouceur);
      const last=rows[rows.length-1];
      if(last) feedCursorRef.current={p_cursor_boost:last.c_boost,p_cursor_ts:last.c_ts,p_cursor_id:last.c_id,p_cursor_dist:last.c_dist};
      const poser=()=>{setFeedItems(prev=>reset?items:[...prev,...items]);setFeedHasMore(rows.length>=LISTING_PAGE_SIZE);setFeedLoading(false);};
      doux(poser);
      // C3 : persiste la page 1 fraîche (reset seul — jamais les pages d'append) sous la
      // signature qui l'a produite. Best-effort : quota plein ⇒ on vit sans cache.
      if(reset&&marketPageCacheOn&&feedSigRef.current){
        try{localStorage.setItem(MARKET_CACHE_KEY(),JSON.stringify({sig:feedSigRef.current,ts:Date.now(),items:items.slice(0,LISTING_PAGE_SIZE)}));}catch(e){}
      }
    },err=>{if(reqId===feedReqRef.current){if(!reset) feedAjoutEnVolRef.current=false;console.error("[LANAC] feed exception:",err&&err.message||err);setFeedError(true);setFeedLoading(false);}});
  },[mergeFeedSellers]);
  // ── EGRESS B1 (01/08/2026) · « Près de chez vous » servi par le SERVEUR ─────────────────
  //  Le défaut d'ÉCHELLE : la page proximité filtrait l'échantillon top-200 (trié par
  //  RÉCENCE) — chercher « proche » dans « récent » rate toute annonce proche mais ancienne
  //  hors top-200. `listings_feed` (p_origin/p_radius, p_sort='distance') rend les plus
  //  PROCHES, bornées (60 = ~3 pages marché), parité de visibilité (p_status='available'
  //  ⇔ status='active', vérifié dans le corps de la RPC). null ⇒ REPLI local (échantillon,
  //  comportement historique) — l'échec réseau ne peut donc que dégrader vers l'existant.
  const [nearFeed,setNearFeed]=useState(null);
  const [nearFeedHasMore,setNearFeedHasMore]=useState(false);
  const [nearFeedLoading,setNearFeedLoading]=useState(false);
  const nearFeedReqRef=useRef(0);
  const nearFeedCursorRef=useRef(null);   // curseur keyset PAR DISTANCE (c_dist) — même famille que le marché
  const nearFeedParamsRef=useRef(null);   // params de la page 1, relus tels quels par loadNearbyMore (cohérence de la pagination)
  const NEARBY_PAGE=60;                   // ~3 pages marché ; les suivantes viennent à la sentinelle
  const loadNearby=useCallback((origin,radiusKm,cat,search,lng)=>{
    const client=lanacSupabaseClient();
    const reqId=++nearFeedReqRef.current;   // 02/08 (audit) : bumpe AVANT toute sortie — sinon une reponse en vol repeignait une origine abandonnee
    nearFeedCursorRef.current=null;
    // ⚠️ SENTINELLE RE-ARMEE (audit 02/08) : `hasMore` restait `true` pendant le vol de la
    //  page 1, donc le noeud sentinelle n'etait ni demonte ni remonte, donc l'observer ne
    //  re-emettait pas ⇒ page 2 PERDUE jusqu'au prochain geste de scroll. Le chemin marche
    //  applique deja cet antidote ; la proximite ne l'avait pas repris.
    setNearFeedHasMore(false);
    // ⚠️ RAYON 0 — PARITE RETABLIE (audit 02/08) : le serveur coercait 0 → 25 km, donc on
    //  affichait des annonces jusqu'a 25 km sous une etiquette « 0 km », la ou le filtre
    //  local d'avant B1 rendait une liste VIDE. Deux instruments qui divergent sur la meme
    //  entree : on garde le comportement historique, sans coercition silencieuse.
    if(!(radiusKm>0)){setNearFeed([]);setNearFeedLoading(false);return Promise.resolve();}
    if(!client||!origin||!Number.isFinite(origin.lat)||!Number.isFinite(origin.lng)){setNearFeed(null);setNearFeedLoading(false);return Promise.resolve();}
    setNearFeedLoading(true);
    const params={
      p_origin_lat:origin.lat,p_origin_lng:origin.lng,
      p_radius_km:radiusKm,   // >0 garanti par la garde ci-dessus (plus de coercition silencieuse)
      p_cat:cat||null,p_search:search||null,p_status:"available",
      p_sort:"distance",p_limit:NEARBY_PAGE,p_lang:lng||"fr",
    };
    nearFeedParamsRef.current=params;
    return client.rpc("listings_feed",params).then(({data,error})=>{
      if(reqId!==nearFeedReqRef.current) return;                       // réponse périmée (rayon/filtre changés)
      setNearFeedLoading(false);
      if(error){console.error("[LANAC] nearby feed error (repli échantillon):",error.message);setNearFeed(null);setNearFeedHasMore(false);return;}
      const rows=Array.isArray(data)?data:[];
      const items=rows.map(r=>listingFromDB(r.item)).filter(Boolean);
      mergeFeedSellers(items);
      const last=rows[rows.length-1];
      if(last) nearFeedCursorRef.current={p_cursor_boost:last.c_boost,p_cursor_ts:last.c_ts,p_cursor_id:last.c_id,p_cursor_dist:last.c_dist};
      setNearFeed(items);
      setNearFeedHasMore(rows.length>=NEARBY_PAGE);
    },err=>{if(reqId===nearFeedReqRef.current){console.error("[LANAC] nearby feed exception (repli échantillon):",err&&err.message||err);setNearFeed(null);setNearFeedHasMore(false);setNearFeedLoading(false);}});
  },[mergeFeedSellers]);
  //  (06/10) `enDouceur` — la page est demandée par le PLEIN ÉCRAN de « près de chez vous », qu'on est en train de regarder :
  //  ses 60 annonces sont posées en TRANSITION (React rend la grille derrière sans retenir le geste), comme la page du marché
  //  (loadFeed). La requête, son curseur et sa garde sont les mêmes ; la sentinelle, elle, appelle sans argument : rien ne change.
  const loadNearbyMore=useCallback((enDouceur=false)=>{
    const client=lanacSupabaseClient();
    const base=nearFeedParamsRef.current, cur=nearFeedCursorRef.current;
    if(!client||!base||!cur) return Promise.resolve();
    const doux=f=>{ if(enDouceur===true&&typeof React.startTransition==="function") React.startTransition(f); else f(); };
    const reqId=++nearFeedReqRef.current;
    return client.rpc("listings_feed",{...base,...cur}).then(({data,error})=>{
      if(reqId!==nearFeedReqRef.current) return;
      if(error){console.error("[LANAC] nearby feed page error:",error.message);setNearFeedHasMore(false);return;}
      const rows=Array.isArray(data)?data:[];
      const items=rows.map(r=>listingFromDB(r.item)).filter(Boolean);
      mergeFeedSellers(items,enDouceur===true);
      const last=rows[rows.length-1];
      if(last) nearFeedCursorRef.current={p_cursor_boost:last.c_boost,p_cursor_ts:last.c_ts,p_cursor_id:last.c_id,p_cursor_dist:last.c_dist};
      doux(()=>{
        setNearFeed(prev=>{
          const seen=new Set((prev||[]).map(i=>String(i.id)));
          return [...(prev||[]),...items.filter(i=>!seen.has(String(i.id)))];  // 0 doublon même si le curseur rejoue une ligne
        });
        setNearFeedHasMore(rows.length>=NEARBY_PAGE);
      });
    },err=>{if(reqId===nearFeedReqRef.current){console.error("[LANAC] nearby feed page exception:",err&&err.message||err);setNearFeedHasMore(false);}});
  },[mergeFeedSellers]);
  useEffect(()=>{
    if(view!=="market") return;
    feedCursorRef.current=null;
    feedSigRef.current=`${listingFilterSig}§${nearMeSig}`; // signature EXACTE de ce chargement (écrite avec le cache)
    // C3 · hydratation instantanée : même signature ⇒ on peint la dernière page 1 connue
    // AVANT le réseau. hasMore=false bloque la sentinelle jusqu'à la page 1 fraîche (qui
    // remplace tout par reset et ré-arme la pagination). TTL 7 j : au-delà on repart à vide.
    if(marketPageCacheOn){
      try{
        const raw=localStorage.getItem(MARKET_CACHE_KEY());
        const c=raw?JSON.parse(raw):null;
        if(c&&c.sig===feedSigRef.current&&Array.isArray(c.items)&&c.items.length
           &&Date.now()-Number(c.ts||0)<7*24*3600*1000){
          setFeedItems(c.items);
          setFeedHasMore(false);
        }
      }catch(e){}
    }
    loadFeed(true);
    const client=lanacSupabaseClient();
    const pr=feedParamsRef.current;
    // Compteur « X résultats » : même protection anti-réponse-périmée que la grille (request id) + reset
    // pendant le chargement pour ne pas laisser un ancien total visible si les filtres changent vite.
    const countReq=++feedCountReqRef.current;
    setFeedTotal(null);
    if(client&&pr&&pr.params) client.rpc("listings_feed_count",pr.params).then(({data})=>{if(countReq===feedCountReqRef.current&&typeof data==="number")setFeedTotal(data);},()=>{});
    // ── Ph5A-2 · FACETTES (compteurs par valeur) — SEULEMENT pour les catégories à sous-filtres.
    //  Débounce 300 ms (guard suffit à neutraliser un tir périmé) + cache MÉMOIRE par signature
    //  (réponse ~200 o, jamais re-tirée pour une signature déjà vue). Miroir des params du feed
    //  ⇒ compteurs == résultats. Flag OFF ou catégorie sans sous-filtres ⇒ facettes = null (rien
    //  d'affiché, 0 egress). AUCUN appel au repos ; borné, débouncé, caché.
    if(facetsOn && client && pr && pr.params && (filterCat==="voiture"||filterCat==="immo"||filterCat==="travaux")){
      const fsig=feedSigRef.current;
      if(facetsCacheRef.current.has(fsig)){ setFacetCounts(facetsCacheRef.current.get(fsig)); }
      else {
        const fReq=++facetsReqRef.current;
        setTimeout(()=>{
          if(fReq!==facetsReqRef.current) return; // filtres changés depuis ⇒ tir périmé, on abandonne
          const c2=lanacSupabaseClient(), pr2=feedParamsRef.current;
          if(!(c2&&pr2&&pr2.params)) return;
          c2.rpc("listings_facets",pr2.params).then(({data})=>{
            if(fReq!==facetsReqRef.current) return;
            const fc=(data&&typeof data==="object")?data:{};
            facetsCacheRef.current.set(fsig,fc); setFacetCounts(fc);
          },()=>{});
        },300);
      }
    } else { setFacetCounts(null); }
  },[listingFilterSig,view,loadFeed,nearMeSig]);
  // Pull-to-refresh : recharge la surface de liste courante à neuf, en réutilisant l'existant.
  //  • market → loadFeed(true) (1re page fraîche, curseur remis à zéro) + recompte du total (miroir de l'effet filtres).
  //  • home   → loadHomeSample() (échantillon récent + mes annonces).
  // Renvoie une Promise résolue à la fin du chargement, pour piloter la durée du spinner. Aucune logique métier nouvelle.
  const refreshCurrentList=useCallback(()=>{
    if(view==="market"){
      feedCursorRef.current=null;
      const p=loadFeed(true);
      const client=lanacSupabaseClient(); const pr=feedParamsRef.current;
      if(client&&pr&&pr.params){
        const countReq=++feedCountReqRef.current; setFeedTotal(null);
        client.rpc("listings_feed_count",pr.params).then(({data})=>{if(countReq===feedCountReqRef.current&&typeof data==="number")setFeedTotal(data);},()=>{});
      }
      return Promise.resolve(p);
    }
    return loadHomeSample(); // (12/10) un rafraîchissement DEMANDÉ relit le fil : l'ordre peut bouger, il l'a voulu
  },[view,loadFeed,loadHomeSample]);
  // ── C1 (17/07/2026) — 6 fonctions du FEED/RECHERCHE remontées de App(), verbatim.
  //  Mesuré : fermetures 0 (sauf currentAlertLabel↔rememberSearch, déplacés ensemble),
  //  tout le nécessaire vit ICI (états de filtres, setters, lang en port). Filets :
  //  S10 (miroirs recherche), S14 (géoloc/PTR), S18/S19/S20 (filtres, marché, recherche).
  //  ⚠ renderFilterContent (ratio 11) N'EST PAS déplacé : ses ports ui/mobileWords/
  //  updateMobileFilter sont déclarés APRÈS l'appel de ce hook (TDZ, la prise de T3-b).
  const requestMyPosition=()=>{
    if(typeof navigator==="undefined"||!navigator.geolocation){setGpsStatus("unavailable");return;}
    setGpsStatus("loading");
    const req=++gpsReqRef.current;
    navigator.geolocation.getCurrentPosition(
      pos=>{if(req!==gpsReqRef.current) return; // un autre choix a été fait pendant la mesure
        const p={lat:pos.coords.latitude,lng:pos.coords.longitude},ts=Date.now();setGpsPos(p);setGpsStatus("on");setNearMe(true);setGpsVille(null);
        ecrireLieu({ville:null}); // (lot B) la position REMPLACE la ville choisie dans la pastille : un seul « dernier choix »
        // A2a : mémorise la position (local seul, jamais serveur) pour la restaurer au prochain boot.
        if(gpsPersistOn){try{localStorage.setItem(GPS_KEY,JSON.stringify({...p,ts}));}catch(e){}}
        //  (lot B) le NOM de la position, pour la pastille. Un échec laisse « Ma position » : rien d'autre n'en dépend.
        //  La fonction /geocode interroge un géocodeur tiers : on ne lui donne que la position ARRONDIE à deux décimales
        //  (≈ 1 km) — assez pour nommer la ville, jamais le point précis de l'appareil.
        fetch(`/geocode?reverse=1&lat=${p.lat.toFixed(2)}&lon=${p.lng.toFixed(2)}&lang=${lang||"fr"}`).then(r=>r.ok?r.json():[]).then(d=>{
          const v=Array.isArray(d)&&d[0];
          if(req!==gpsReqRef.current||!v||typeof v.name!=="string"||!v.name) return;
          setGpsVille({nom:v.name,pays:v.cc||"all"});
          if(gpsPersistOn){try{localStorage.setItem(GPS_KEY,JSON.stringify({...p,ville:v.name,pays:v.cc||"all",ts}));}catch(e){}}
        }).catch(()=>{});},
      err=>{if(req!==gpsReqRef.current) return;setGpsStatus(err&&err.code===1?"denied":"unavailable");setNearMe(false);},
      {enableHighAccuracy:false,timeout:10000,maximumAge:300000}
    );
  };
  const bumpFavoritesCount=(id,delta)=>{
    const same=x=>String(x.id)===String(id);
    const bump=i=>({...i,favoritesCount:Math.max(0,Number(i.favoritesCount||0)+delta)});
    setItems(p=>p.map(i=>same(i)?bump(i):i));
    // Identité du tableau préservée si l'annonce n'est pas dans la grille → aucun re-render du marché.
    setFeedItems(p=>p.some(same)?p.map(i=>same(i)?bump(i):i):p);
    setSel(prev=>prev&&same(prev)?bump(prev):prev);
  };
  const currentAlertLabel=()=>{
    const parts=[];
    if(filterCat) parts.push(T[lang].home[filterCat==="immo"?"immo":filterCat==="voiture"?"cars":filterCat==="travaux"?"works":"others"]);
    if(filterAdType==="sale") parts.push(tx("tax.achat_ab4f89",lang));
    if(filterAdType==="rent") parts.push(tx("tax.location_5ac955",lang));
    //  (04/10, A17) les sous-filtres : l'alerte les applique désormais (le serveur aussi) — son nom doit les dire.
    if(filterOther){const _c=OTHER_MAP[filterOther];parts.push(_c?(_c.label[lang]||_c.label.fr):filterOther);}
    if(filterOtherSub){const _s=(OTHER_SUBCATS[filterOther]||[]).find(x=>x.id===filterOtherSub);parts.push(_s?(_s.label[lang]||_s.label.fr):filterOtherSub);}
    if(filterPropType) parts.push(optionLabel("immo",filterPropType,lang));
    if(filterRooms) parts.push(`${filterRooms}+ ${T[lang].card.rooms}`);
    if(filterBrand) parts.push(filterBrand);
    if(filterFuel) parts.push(optionLabel("fuel",filterFuel,lang));
    if(filterWorkType) parts.push(optionLabel("work",filterWorkType,lang));
    if(filterCity!=="all") parts.push(radiusKm>0?`${filterCity} +${radiusKm} km`:filterCity);
    if(maxP<5000000) parts.push(`≤ ${maxP.toLocaleString(localeForLang(ACTIVE_LANG))} €`);
    if(srch) parts.push(`"${srch}"`);
    return parts.join(" · ")||tx("tax.toutes-nouvelles-annonce_4b4b4c",lang);
  };
  const rememberSearch=(label,filters={})=>{
    if(!me) return;
    const clean=(label||filters.query||"").trim();
    if(clean.length<2&&!filters.cat) return;
    const memory={
      id:newUUID(),
      label:clean||currentAlertLabel(),
      cat:filters.cat||null,
      adType:filters.adType||null,
      other:filters.other||null,
      country:filters.country||"all",
      city:filters.city||"all",
      radius:Number(filters.radius||0),
      coords:(filters.coords&&Number.isFinite(filters.coords.lat)&&Number.isFinite(filters.coords.lng))?{lat:filters.coords.lat,lng:filters.coords.lng}:null,
      max:Number(filters.max||5000000),
      query:filters.query||clean,
      ts:Date.now()
    };
    //  L'écran d'abord (mise à jour PURE : la même recherche remonte, dix-huit au plus), la base ensuite — EN FILE.
    //   (C11 (i) : avant, la liste partait d'un état pas encore à jour — deux appuis à 19 ms écrivaient deux lignes — et chaque
    //   recherche tombée hors des dix-huit était supprimée une par une, hors de la passerelle, son échec avalé.)
    setSearchMemory(prev=>fusionnerHistorique([memory],prev||[]));
    const uid=me?.id;
    if(!uid) return;
    //  C7 · T20 — LA LIGNE D'HISTORIQUE NE SE PERD PLUS EN SILENCE. `query` reprend la recherche telle quelle : un texte collé
    //   au-delà du plafond du serveur (T19) serait refusé. On ne DÉRANGE pas le membre — sa recherche, elle, a fonctionné, et
    //   l'entrée reste dans son historique local — mais l'écart est DIT.
    const rangee={id:memory.id,...alertToDB(memory)};
    const trop=lanacTropLong("search_history",{...rangee,user_id:uid});
    if(trop){try{window.__lncReport?.("search_history_too_long",`${trop.champ} : ${trop.longueur} caractères (plafond ${trop.plafond}) — gardée en local`);}catch(_){} return;}
    histFileRef.current.enAttente.set(memory.id,memory);
    histEnFile(uid,async()=>{
      const r=await lanacRpc({what:"historique de recherche",name:"remember_search",args:{p_entry:rangee},idempotent:true});
      histFileRef.current.enAttente.delete(memory.id);
      if(!r.ok||histFileRef.current.user!==uid) return;
      const d=r.data||{};
      histRetenirEmpreinte(uid,d.fp);
      //  la base a pu garder un AUTRE identifiant (la même recherche retenue au même instant ailleurs) : l'écran s'y aligne
      const ligne=d.row?alertFromDB(d.row):null;
      if(ligne) setSearchMemory(prev=>fusionnerHistorique([ligne],(prev||[]).filter(x=>x.id!==memory.id)));
    });
  };
  //  Oublier UNE recherche (le « × » de chaque entrée) — par la commande, en file, jamais un `.catch` qui avale.
  const forgetSearch=id=>{
    setSearchMemory(prev=>(prev||[]).filter(x=>x.id!==id));
    const uid=me?.id;
    if(!uid||!isUUID(id)) return;
    histEnFile(uid,async()=>{
      const r=await lanacRpc({what:"oubli d'une recherche",name:"forget_search",args:{p_id:id},idempotent:true});
      if(r.ok&&histFileRef.current.user===uid) histRetenirEmpreinte(uid,r.data?.fp);
    });
  };
  //  « Tout effacer », en deux temps — le patron « Annuler » de l'application : l'écran se vide TOUT DE SUITE ; la base est
  //   effacée à l'échéance du bandeau (ou dès qu'un autre geste le règle) ; « Annuler » rend la liste sans que rien soit parti.
  //   Un échec rend la liste (rien n'est perdu) et l'appelant le DIT.
  const clearSearchMemoryLocal=()=>{const avant=searchMemory;setSearchMemory([]);return avant;};
  const restoreSearchMemory=avant=>setSearchMemory(prev=>fusionnerHistorique(prev||[],avant||[]));
  const commitClearSearchHistory=avant=>{
    const uid=me?.id;
    if(!uid) return Promise.resolve(true);
    return histEnFile(uid,async()=>{
      const r=await lanacRpc({what:"historique de recherche effacé",name:"clear_search_history",idempotent:true});
      if(histFileRef.current.user!==uid) return true;
      if(!r.ok){restoreSearchMemory(avant);return false;}
      histRetenirEmpreinte(uid,r.data?.fp);
      return true;
    });
  };
  // Routage data-driven : si aucun mot-clé de catégorie n'est reconnu, on demande au serveur
  // la répartition par catégorie des résultats du texte, et on route si une catégorie domine.
  // Dégradable : tout échec/ambiguïté → null → comportement actuel (toutes catégories).
  const inferSearchCategory=async q=>{
    const query=(q||"").trim(); if(!query) return null;
    const client=lanacSupabaseClient(); if(!client||typeof client.rpc!=="function") return null;
    try{
      const {data}=await client.rpc("listings_search_category_counts",{p_search:query,p_lang:lang});
      const total=Number(data&&data.total)||0; if(!total) return null;
      const cats=[["immo",Number(data.immo)||0],["voiture",Number(data.voiture)||0],["travaux",Number(data.travaux)||0],["autres",Number(data.autres)||0]].sort((a,b)=>b[1]-a[1]);
      const topCat=cats[0][0],topN=cats[0][1],secondN=cats[1][1];
      if(topN===total) return topCat;                                        // 100 % d'une catégorie → routage sûr
      if(total>=3 && topN>=Math.ceil(total*0.6) && topN>=secondN*1.5) return topCat; // dominance nette
      return null;
    }catch(e){ return null; }
  };
  const hydrateMobileFilters=(cat=filterCat)=>({
    cat:cat||null,
    adType:filterAdType||null,
    propType:filterPropType||"",
    rooms:filterRooms?String(filterRooms):"",
    brand:filterBrand||"",
    fuel:filterFuel||"",
    workType:filterWorkType||"",
    other:filterOther||"",
    otherSub:filterOtherSub||"",
    country:filterCountry||"all",
    city:filterCity||"all",
    coords:filterCoords||null,
    radius:String(radiusKm||0),
    max:maxP<5000000?String(maxP):"",
    near:radiusKm>0,
    query:srch||""
  });
  // C1 (complément) — searchDraftFor : préremplit le brouillon de recherche avec la ville
  //  du profil (ou géo-IP) si aucun lieu n'est encore posé. Appelle hydrateMobileFilters
  //  (ci-dessus) ; fermeture 0. Retourné (2 usages App).
  //  (02/10, lot B) PLUS DE LIEU POSÉ SANS GESTE. Avant, le brouillon recevait d'office « ville du profil + 25 km » : toute
  //  recherche faite par l'onglet Rechercher était filtrée STRICTEMENT à 25 km sans que le membre l'ait demandé (0 résultat
  //  loin des annonces), sous le nom du profil et parfois avec les coordonnées de l'IP. Le lieu du membre trie désormais la
  //  liste (les plus proches d'abord, puis « Plus loin ») ; seul un lieu que la recherche porte elle-même reste un filtre.
  const searchDraftFor=(cat=filterCat)=>hydrateMobileFilters(cat);
  return {noterVueAnnonce,requestMyPosition,bumpFavoritesCount,currentAlertLabel,rememberSearch,forgetSearch,clearSearchMemoryLocal,restoreSearchMemory,commitClearSearchHistory,inferSearchCategory,hydrateMobileFilters,searchDraftFor,
    loadFeed,refreshCurrentList,
    restorePrevFilters,resetMarketFilters,
    ptrBarRef,gestureLockRef,attachPullToRefresh,HOME_SAMPLE_SIZE,homeSampleFor,homeTenteFor,canSeeListingRef,homeSampleReqRef,hydrateViewCounts,loadHomeSample,filPerso,reglerPersoDuFil,filProfil,chargerFilProfil,filLectureMs,rangDuFil,
    listingFilterSig,nearMeSig,feedParamsRef,mobileSearchInputRef,
    selRef,feedItemsRef,itemsRef,
    items,setItems,sel,setSel,filterCat,setFilterCat,filterAdType,setFilterAdType,filterOther,setFilterOther,
    filterOtherSub,setFilterOtherSub,filterCountry,setFilterCountry,filterCity,setFilterCity,radiusKm,setRadiusKm,
    filterCoords,setFilterCoords,srch,setSrch,maxP,setMaxP,filterStatus,setFilterStatus,feedSortPref,setFeedSortPref,facetCounts,mapHover,setMapHover,
    aiInp,setAiInp,aiLoad,setAiLoad,aiResults,setAiResults,visualSearch,setVisualSearch,
    availabilityWatch,setAvailabilityWatch,searchMemory,setSearchMemory,topSearch,setTopSearch,searchTab,setSearchTab,
    browseCat,setBrowseCat,browseMode,setBrowseMode,mobileFilterPanel,setMobileFilterPanel,
    mobileFilterReturn,setMobileFilterReturn,mobileFilters,setMobileFilters,filterRooms,setFilterRooms,
    filterPropType,setFilterPropType,filterBrand,setFilterBrand,filterFuel,setFilterFuel,filterWorkType,setFilterWorkType,
    listingVisibleCount,setListingVisibleCount,nearSearch,setNearSearch,nearCat,setNearCat,nearRadiusKm,setNearRadiusKm,
    gpsPos,setGpsPos,gpsStatus,setGpsStatus,nearMe,setNearMe,showGeoPrompt,setShowGeoPrompt,ipGeo,setIpGeo,
    gpsVille,lieuPref,clearMyPosition,choisirLieuVille,choisirRayonLieu,oublierLieuChoisi,
    feedItems,setFeedItems,feedLoading,setFeedLoading,feedHasMore,setFeedHasMore,feedError,setFeedError,
    nearFeed,nearFeedHasMore,nearFeedLoading,loadNearby,loadNearbyMore,
    feedTotal,setFeedTotal,catCounts,setCatCounts,mapPoints,setMapPoints,searchSuggestItems,setSearchSuggestItems,
    ptrRefreshing,setPtrRefreshing,ptrSkClosing,setPtrSkClosing,ptrSkTop,setPtrSkTop,searchFocused,setSearchFocused};
}
export { useFeedState, LISTING_PAGE_SIZE, sondeDuFil, signatureDuFil, sondeGardee, avantLaSession, filPersoOn, reglerFilPerso, oublierInteretsDAvant, rangDuFil, HOME_CACHE_PLAFOND };
